// src/pages/TokenDashboard.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

import { api } from "../lib/api";
import {
  buildCustomBurnArgs,
  buildCustomTokenInfoFromOverview,
  confirmCustomTokenAction,
  getCustomActionCapabilities,
  isCustomTokenInfo,
  prepareCustomTokenAction,
} from "../lib/customTokenActions";
import { useAuth } from "../auth/useAuth";
import {
  switchActiveWalletChain,
  sendActiveWalletTransaction,
} from "../wallet/walletClient";
import {
  hasPaidFeatureAccess,
  isPaidPlan as isBillingPaidPlan,
  normalizePlanCode,
} from "../components/billing/plans";

function safeApiError(err, fallback = "Something went wrong.") {
  const status = err?.response?.status;
  const message =
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    err?.message ||
    "";

  if (!message) return fallback;

  // Never expose backend internals or stack-like error details to users
  const lowered = String(message).toLowerCase();

  if (
    lowered.includes("prisma") ||
    lowered.includes("invocation") ||
    lowered.includes("stack") ||
    lowered.includes("database") ||
    lowered.includes("can't reach database server") ||
    lowered.includes("neon.tech") ||
    status >= 500
  ) {
    return fallback;
  }

  return String(message);
}

function isNetworkOfflineError(err) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return true;
  }

  const message = String(
    err?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    "",
  ).toLowerCase();

  return (
    message.includes("network error") ||
    message.includes("failed to fetch") ||
    message.includes("connection terminated unexpectedly")
  );
}

function shorten(value) {
  if (!value || typeof value !== "string") return "-";
  if (value.length <= 12) return value;
  return `${value.slice(0, 6)}…${value.slice(-4)}`;
}

function formatNumber(value, emptyText = "-") {
  if (value == null || value === "") return emptyText;

  const str = String(value);

  if (str.length > 18) {
    return str;
  }

  const n = Number(value);
  if (!Number.isFinite(n)) return str;

  return n.toLocaleString();
}

function formatLargeMetricValue(value, emptyText = "-") {
  if (value == null || value === "") return emptyText;

  const raw = String(value).trim();
  if (!raw) return emptyText;

  const normalized = raw.replace(/,/g, "");
  if (!/^\d+$/.test(normalized)) return raw;

  if (normalized.length <= 15) {
    const n = Number(normalized);
    return Number.isFinite(n) ? n.toLocaleString() : raw;
  }

  const head = normalized.slice(0, 4);
  const tail = normalized.slice(-4);

  return `${head}…${tail}`;
}

function formatUsd(value, emptyText = "Unavailable") {
  if (value == null || value === "") return emptyText;
  const n = Number(value);
  if (!Number.isFinite(n)) return emptyText;
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function formatRoyaltyPercent(value, emptyText = "-") {
  if (value == null || value === "") return emptyText;

  const n = Number(value);
  if (!Number.isFinite(n)) return emptyText;

  const percent = n / 100;

  return `${percent.toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })}%`;
}

function formatUsageStat(used, limit) {
  const normalizedUsed = used ?? 0;

  if (limit == null || limit === "") return String(normalizedUsed);
  if (Number(limit) < 0) return `${normalizedUsed} / Unlimited`;

  return `${normalizedUsed} / ${limit}`;
}


function buildFallbackTrendSeries(currentValue, days = 7) {
  const safeValue = Number(currentValue || 0);
  const today = new Date();

  return Array.from({ length: days }, (_, index) => {
    const pointDate = new Date(today);
    pointDate.setDate(today.getDate() - (days - 1 - index));

    return {
      label: new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "2-digit",
      }).format(pointDate),
      value: index === days - 1 ? safeValue : 0,
    };
  });
}

function toLower(v) {
  return String(v || "").toLowerCase();
}

function normalizeNetwork(v) {
  return String(v || "").trim().toLowerCase();
}

function isEvmAddress(v) {
  return typeof v === "string" && /^0x[a-fA-F0-9]{40}$/.test(String(v).trim());
}

function normalizeTokenType(v) {
  const t = String(v || "").trim().toUpperCase();
  if (t === "ERC20" || t === "ERC721" || t === "ERC1155") return t;
  return t || "-";
}

function getCustomMetadataFromDeployment(deployment) {
  const value = deployment?.customMetadata;

  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value;
  }

  return {};
}

function isStandardTokenType(value) {
  const clean = String(value || "").trim().toUpperCase();
  return clean === "ERC20" || clean === "ERC721" || clean === "ERC1155";
}

function deriveDashboardContractType({ deployment, token, tokenReadInfo }) {
  const customMetadata = getCustomMetadataFromDeployment(deployment);

  const capabilities =
    deployment?.capabilities ||
    tokenReadInfo?.capabilities ||
    customMetadata.capabilities ||
    {};

  const sourceMode = String(
    deployment?.sourceMode ||
    tokenReadInfo?.sourceMode ||
    customMetadata.sourceMode ||
    "",
  )
    .trim()
    .toLowerCase();

  const isCustomDeployment =
    sourceMode === "custom" ||
    Boolean(deployment?.customVersionId || customMetadata.customVersionId);

  const rawTokenType = normalizeTokenType(
    token?.tokenType ||
    deployment?.tokenType ||
    tokenReadInfo?.tokenType ||
    customMetadata.tokenType,
  );

  const capErc1155 =
    capabilities?.erc1155 === true ||
    capabilities?.tokenType === "ERC1155" ||
    capabilities?.standardTokenType === "ERC1155";

  const capErc721 =
    capabilities?.erc721 === true ||
    capabilities?.tokenType === "ERC721" ||
    capabilities?.standardTokenType === "ERC721";

  const capErc20 =
    capabilities?.erc20 === true ||
    capabilities?.tokenType === "ERC20" ||
    capabilities?.standardTokenType === "ERC20";

  const capOwnable =
    capabilities?.ownable === true ||
    capabilities?.transferOwnership === true;

  const capAccessControl =
    capabilities?.accessControl === true ||
    capabilities?.grantRole === true ||
    capabilities?.revokeRole === true ||
    capabilities?.hasRole === true;

  const explicitIsToken =
    typeof deployment?.isToken === "boolean"
      ? deployment.isToken
      : typeof customMetadata?.isToken === "boolean"
        ? customMetadata.isToken
        : null;

  let isToken;

  if (explicitIsToken != null) {
    isToken = explicitIsToken;
  } else if (capErc1155 || capErc721 || capErc20) {
    isToken = true;
  } else if (isCustomDeployment && (capOwnable || capAccessControl)) {
    isToken = false;
  } else {
    isToken = isStandardTokenType(rawTokenType);
  }

  let displayType =
    deployment?.displayType ||
    customMetadata.displayType ||
    tokenReadInfo?.displayType ||
    "";

  if (!displayType) {
    if (isCustomDeployment) {
      if (capErc1155) displayType = "Custom ERC1155";
      else if (capErc721) displayType = "Custom ERC721";
      else if (capErc20) displayType = "Custom ERC20";
      else if (capOwnable) displayType = "Custom Ownable";
      else if (capAccessControl) displayType = "Custom AccessControl";
      else displayType = "Custom Contract";
    } else if (isToken && isStandardTokenType(rawTokenType)) {
      displayType = rawTokenType;
    } else {
      displayType = "Contract";
    }
  }

  const starterTemplate =
    deployment?.starterTemplate ||
    customMetadata.starterTemplate ||
    "";

  const detectedContractType =
    deployment?.detectedContractType ||
    customMetadata.detectedContractType ||
    "";

  const capabilityLabels =
    Array.isArray(deployment?.capabilityLabels)
      ? deployment.capabilityLabels
      : Array.isArray(customMetadata.capabilityLabels)
        ? customMetadata.capabilityLabels
        : Array.isArray(capabilities?.capabilityLabels)
          ? capabilities.capabilityLabels
          : [];

  const tokenType =
    capErc1155
      ? "ERC1155"
      : capErc721
        ? "ERC721"
        : capErc20
          ? "ERC20"
          : isToken && isStandardTokenType(rawTokenType)
            ? rawTokenType
            : rawTokenType;

  return {
    tokenType,
    isToken,
    displayType,
    starterTemplate,
    detectedContractType,
    capabilityLabels,
  };
}

function isIntegerString(v) {
  if (v == null) return false;
  return /^[0-9]+$/.test(String(v).trim());
}

function isDecimalString(v) {
  if (v == null) return false;
  return /^[0-9]+(\.[0-9]+)?$/.test(String(v).trim());
}

const MAX_BURN_ERC20_HUMAN_UI = 1000000;
const MAX_BURN_ERC1155_QTY_UI = 1000;

const ROLE_OPTIONS = [
  {
    value: "MINTER_ROLE",
    label: "Minter",
    description: "Can mint new tokens when minting is enabled.",
    module: "mintable",
  },
  {
    value: "PAUSER_ROLE",
    label: "Pauser",
    description: "Can pause or unpause the contract when pausable is enabled.",
    module: "pausable",
  },
];

function normalizeRoleKind(value) {
  const clean = String(value || "").trim();

  if (clean === "grantRole" || clean === "revokeRole") {
    return clean;
  }

  return "grantRole";
}

function humanizeWalletActionError(err, fallback = "Transaction failed.") {
  const code = err?.code;
  const nestedCode = err?.info?.error?.code;
  const message = String(
    err?.shortMessage ||
    err?.reason ||
    err?.message ||
    err?.info?.error?.message ||
    "",
  ).toLowerCase();

  const isRejected =
    code === "ACTION_REJECTED" ||
    code === 4001 ||
    nestedCode === 4001 ||
    nestedCode === 5000 ||
    message.includes("user denied") ||
    message.includes("user rejected") ||
    message.includes("rejected") ||
    message.includes("denied transaction signature");

  if (isRejected) {
    return "Action was cancelled in your wallet.";
  }

  return safeApiError(err, fallback);
}

async function confirmWithRetry(fn, { attempts = 6, delayMs = 2000 } = {}) {
  let lastErr = null;

  for (let i = 0; i < attempts; i += 1) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  throw lastErr;
}

function normalizeWhen(rec) {
  return rec?.confirmedAt || rec?.deployedAt || rec?.createdAt || null;
}

function compareNewestFirst(a, b) {
  const ad = normalizeWhen(a);
  const bd = normalizeWhen(b);
  const at = ad ? new Date(ad).getTime() : 0;
  const bt = bd ? new Date(bd).getTime() : 0;
  return bt - at;
}

function getLiquidityStatusTone(syncStatus) {
  const s = String(syncStatus || "").toLowerCase();

  if (s === "ok") {
    return "border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-900";
  }
  if (s === "unsupported_testnet") {
    return "border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 text-amber-900";
  }
  if (s === "not_found") {
    return "border-slate-200 bg-gradient-to-r from-slate-50 to-white text-slate-900";
  }
  if (s === "error") {
    return "border-red-200 bg-gradient-to-r from-red-50 to-rose-50 text-red-900";
  }

  return "border-slate-200 bg-gradient-to-r from-slate-50 to-white text-slate-900";
}

function isTestnetLiquidityStatus(syncStatus) {
  return String(syncStatus || "").toLowerCase() === "unsupported_testnet";
}

function isNoPoolLiquidityStatus(syncStatus) {
  return String(syncStatus || "").toLowerCase() === "not_found";
}

function isLiquidityAvailable(syncStatus) {
  return String(syncStatus || "").toLowerCase() === "ok";
}

function formatLiquidityNumber(value, locked, emptyText = "No data") {
  if (locked) return "Starter or Pro";
  if (value == null || value === "") return emptyText;
  return formatNumber(value);
}

function formatLiquidityUsd(value, locked, emptyText = "No data") {
  if (locked) return "Starter or Pro";
  if (value == null || value === "") return emptyText;
  return formatUsd(value);
}

function cn(...values) {
  return values.filter(Boolean).join(" ");
}

function Surface({ className = "", children }) {
  return (
    <div
      className={cn(
        "rounded-[28px] border border-slate-200/80 bg-white shadow-[0_18px_55px_rgba(15,23,42,0.06)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

function SectionCard({
  title,
  subtitle,
  right,
  children,
  className = "",
  eyebrow,
}) {
  return (
    <Surface className={cn("overflow-hidden p-6 sm:p-7", className)}>
      <div className="mb-6 flex flex-col gap-4 border-b border-slate-100 pb-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          {eyebrow ? (
            <div className="inline-flex rounded-full border border-[#E7D8AC] bg-[#FFF9EA] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8A6A1F]">
              {eyebrow}
            </div>
          ) : null}

          <h2 className={cn("text-xl font-semibold tracking-tight text-slate-950", eyebrow ? "mt-3" : "")}>
            {title}
          </h2>

          {subtitle ? (
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
              {subtitle}
            </p>
          ) : null}
        </div>

        {right ? <div className="shrink-0">{right}</div> : null}
      </div>

      {children}
    </Surface>
  );
}

function MetricCard({
  label,
  value,
  hint,
  tone = "neutral",
  valueClassName = "",
  compactValue = false,
}) {
  const toneClass =
    tone === "brand"
      ? "border-[#E7D8AC] bg-[#FFF9EA]"
      : tone === "success"
        ? "border-emerald-200 bg-emerald-50"
        : tone === "warning"
          ? "border-[#E7D8AC] bg-[#FFF9EA]"
          : "border-slate-200 bg-slate-50";

  const labelClass =
    tone === "brand" || tone === "warning"
      ? "text-[#8A6A1F]"
      : tone === "success"
        ? "text-emerald-700"
        : "text-slate-500";

  return (
    <div className={cn("rounded-[24px] border p-5 shadow-sm", toneClass)}>
      <div
        className={cn(
          "text-[11px] font-semibold uppercase tracking-[0.16em]",
          labelClass,
        )}
      >
        {label}
      </div>

      <div
        className={cn(
          "mt-4 break-all font-semibold tracking-tight text-slate-950 leading-tight",
          compactValue ? "text-lg leading-7" : "text-xl sm:text-2xl leading-8",
          valueClassName,
        )}
        title={
          typeof value === "string" || typeof value === "number"
            ? String(value)
            : undefined
        }
      >
        {value}
      </div>

      {hint ? (
        <p className="mt-2 text-xs leading-5 text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}

function InlineNotice({ title, subtitle, tone = "neutral" }) {
  const toneClass =
    tone === "warning"
      ? "border-[#E7D8AC] bg-[#FFF9EA] text-[#6F5318]"
      : tone === "error"
        ? "border-rose-200 bg-rose-50 text-rose-900"
        : tone === "success"
          ? "border-emerald-200 bg-emerald-50 text-emerald-900"
          : "border-slate-200 bg-slate-50 text-slate-900";

  return (
    <div className={cn("rounded-2xl border px-4 py-3.5", toneClass)}>
      <div className="text-sm font-semibold">{title}</div>
      {subtitle ? (
        <div className="mt-1 text-xs leading-5 opacity-80">{subtitle}</div>
      ) : null}
    </div>
  );
}

function Badge({ children, tone = "neutral" }) {
  const toneClass =
    tone === "success"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : tone === "warning"
        ? "border-[#E7D8AC] bg-[#FFF9EA] text-[#8A6A1F]"
        : tone === "brand"
          ? "border-[#E7D8AC] bg-[#FFF9EA] text-[#7A5A17]"
          : "border-slate-200 bg-white text-slate-600";

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold",
        toneClass,
      )}
    >
      {children}
    </span>
  );
}

export default function TokenDashboard() {
  const { network: networkParam, address: addressParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthed, authReady = true } = useAuth();

  const [activeWallet, setActiveWallet] = useState(() =>
    String(localStorage.getItem("chainforge_wallet_address") || "")
      .trim()
      .toLowerCase(),
  );
  const network = normalizeNetwork(networkParam);
  const address = String(addressParam || "").trim();
  const hasValidParams = Boolean(network) && isEvmAddress(address);

  const activeHistoryTokenKey = useMemo(() => {
    return activeWallet
      ? `chainforge_active_history_token_${activeWallet}`
      : "";
  }, [activeWallet]);

  const openedFromHistory = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("source") === "history";
  }, [location.search]);

  useEffect(() => {
    const syncWallet = () => {
      const nextWallet = String(localStorage.getItem("chainforge_wallet_address") || "")
        .trim()
        .toLowerCase();

      setActiveWallet((prev) => (prev === nextWallet ? prev : nextWallet));
    };

    syncWallet();

    window.addEventListener("focus", syncWallet);
    window.addEventListener("storage", syncWallet);
    window.addEventListener("chainforge:wallet-changed", syncWallet);

    return () => {
      window.removeEventListener("focus", syncWallet);
      window.removeEventListener("storage", syncWallet);
      window.removeEventListener("chainforge:wallet-changed", syncWallet);
    };
  }, []);

  const ensureWalletOnChain = useCallback(async (chainId) => {
    await switchActiveWalletChain(chainId);
  }, []);

  const sendUserTransaction = useCallback(
    async (tx, expectedWallet, chainId) => {
      return sendActiveWalletTransaction(tx, {
        chainId,
        expectedAddress: expectedWallet,
      });
    },
    [],
  );

  useEffect(() => {
    if (!isAuthed) return;
    if (!hasValidParams) return;
    if (!activeHistoryTokenKey) return;
    if (!openedFromHistory) return;

    try {
      sessionStorage.setItem(
        activeHistoryTokenKey,
        JSON.stringify({
          network,
          address,
        }),
      );
    } catch {
      // ignore storage errors
    }
  }, [isAuthed, hasValidParams, activeHistoryTokenKey, openedFromHistory, network, address]);


  useEffect(() => {
    if (!openedFromHistory) return;
    if (!hasValidParams) return;
    if (!activeWallet) return;

    try {
      const storageKey = `chainforge_active_history_token_${activeWallet}`;
      const raw = sessionStorage.getItem(storageKey);

      if (!raw) {
        navigate("/history", { replace: true });
        return;
      }

      const parsed = JSON.parse(raw);
      if (!parsed?.network || !parsed?.address) {
        navigate("/history", { replace: true });
        return;
      }

      const targetNetwork = String(parsed.network).trim().toLowerCase();
      const targetAddress = String(parsed.address).trim().toLowerCase();
      const currentNetwork = String(network || "").trim().toLowerCase();
      const currentAddress = String(address || "").trim().toLowerCase();

      if (
        targetNetwork !== currentNetwork ||
        targetAddress !== currentAddress
      ) {
        navigate(`/token/${targetNetwork}/${parsed.address}?source=history`, {
          replace: true,
        });
      }
    } catch {
      navigate("/history", { replace: true });
    }
  }, [openedFromHistory, activeWallet, hasValidParams, network, address, navigate]);


  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState("");
  const [tokenReadInfo, setTokenReadInfo] = useState(null);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");

  const [nftInsights, setNftInsights] = useState(null);
  const [nftInsightsLoading, setNftInsightsLoading] = useState(false);
  const [nftInsightsError, setNftInsightsError] = useState("");

  const [accountUsage, setAccountUsage] = useState(null);
  const [accountUsageLoading, setAccountUsageLoading] = useState(false);
  const [accountUsageError, setAccountUsageError] = useState("");
  const [actionModal, setActionModal] = useState(null);
  const [actionAmount, setActionAmount] = useState("");
  const [actionTokenId, setActionTokenId] = useState("");
  const [acceptedActionRisk, setAcceptedActionRisk] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionStatus, setActionStatus] = useState("");

  const [roleActionKind, setRoleActionKind] = useState("grantRole");
  const [roleName, setRoleName] = useState("MINTER_ROLE");
  const [roleAccount, setRoleAccount] = useState("");
  const [roleLoading, setRoleLoading] = useState(false);
  const [roleStatus, setRoleStatus] = useState("");

  const [ownershipNewOwner, setOwnershipNewOwner] = useState("");
  const [ownershipLoading, setOwnershipLoading] = useState(false);
  const [ownershipStatus, setOwnershipStatus] = useState("");

  const [walletBalanceTokenId, setWalletBalanceTokenId] = useState("");
  const [walletBalanceValue, setWalletBalanceValue] = useState(null);
  const [walletBalanceLoading, setWalletBalanceLoading] = useState(false);
  const [walletBalanceError, setWalletBalanceError] = useState("");

  const dashboardLoadRef = useRef({
    key: "",
    at: 0,
    inFlight: false,
  });

  const overviewRequestSeqRef = useRef(0);

  useEffect(() => {
    overviewRequestSeqRef.current += 1;

    dashboardLoadRef.current = {
      key: "",
      at: 0,
      inFlight: false,
    };

    setOverview(null);
    setOverviewError("");
    setOverviewLoading(false);

    setTokenReadInfo(null);

    setHistory([]);
    setHistoryError("");
    setHistoryLoading(false);

    setNftInsights(null);
    setNftInsightsError("");
    setNftInsightsLoading(false);

    setAccountUsage(null);
    setAccountUsageError("");
    setAccountUsageLoading(false);

    setWalletBalanceValue(null);
    setWalletBalanceError("");
    setWalletBalanceTokenId("");
    setWalletBalanceLoading(false);

    setRoleActionKind("grantRole");
    setRoleName("MINTER_ROLE");
    setRoleAccount("");
    setRoleStatus("");
    setRoleLoading(false);

    setOwnershipNewOwner("");
    setOwnershipStatus("");
    setOwnershipLoading(false);
  }, [activeWallet, isAuthed, network, address]);

  const loadOverview = useCallback(
    async (options = false) => {
      if (!hasValidParams) return;
      if (!isAuthed) return;

      const refresh =
        typeof options === "boolean"
          ? options
          : Boolean(options?.refresh);

      const tokenRefresh =
        typeof options === "boolean"
          ? options
          : Boolean(options?.tokenRefresh);
      const requestSeq = overviewRequestSeqRef.current + 1;
      overviewRequestSeqRef.current = requestSeq;
      setOverviewLoading(true);
      setOverviewError("");

      try {
        const res = await api.get("/dashboard/overview", {
          params: {
            network,
            address,
            refresh: refresh ? "true" : "false",
          },
          headers: { "Cache-Control": "no-store" },
        });

        const baseOverview = res.data || null;

        if (overviewRequestSeqRef.current !== requestSeq) {
          return;
        }

        setOverview((prev) => {
          if (!prev) return baseOverview;
          if (!baseOverview) return prev;

          const previousVerification = String(
            prev?.verification?.status ||
            prev?.deployment?.verificationStatus ||
            "",
          ).toLowerCase();

          const nextVerification = String(
            baseOverview?.verification?.status ||
            baseOverview?.deployment?.verificationStatus ||
            "",
          ).toLowerCase();

          const shouldPreserveVerified =
            previousVerification === "verified" &&
            nextVerification !== "verified";

          if (!shouldPreserveVerified) {
            return baseOverview;
          }

          return {
            ...baseOverview,
            verification: prev.verification || baseOverview.verification,
            deployment: {
              ...(baseOverview.deployment || {}),
              verificationStatus:
                prev?.deployment?.verificationStatus ||
                baseOverview?.deployment?.verificationStatus,
              verifiedAt:
                prev?.deployment?.verifiedAt ||
                baseOverview?.deployment?.verifiedAt,
            },
          };
        });

        try {
          const tokenRes = await api.get(`/token/${address}`, {
            params: {
              network,
              viewer: activeWallet || "",
              refresh: tokenRefresh ? "true" : "false",
              noCache: tokenRefresh ? "true" : "false",
            },
            headers: { "Cache-Control": "no-store" },
          });

          if (overviewRequestSeqRef.current === requestSeq) {
            setTokenReadInfo(tokenRes.data || null);
          }
        } catch {
          if (overviewRequestSeqRef.current === requestSeq) {
            setTokenReadInfo(null);
          }
        }
      } catch (err) {
        if (overviewRequestSeqRef.current !== requestSeq) {
          return;
        }

        setOverview(null);
        setOverviewError(
          isNetworkOfflineError(err)
            ? "You appear to be offline. Reconnect and try again."
            : safeApiError(err, "Dashboard overview is temporarily unavailable."),
        );
      } finally {
        if (overviewRequestSeqRef.current === requestSeq) {
          setOverviewLoading(false);
        }
      }
    },
    [hasValidParams, isAuthed, address, network, activeWallet],
  );

  const loadHistory = useCallback(async () => {
    if (!isAuthed) {
      setHistory([]);
      setHistoryError("");
      return;
    }

    setHistoryLoading(true);
    setHistoryError("");

    try {
      const res = await api.get("/token-actions", {
        params: {
          mine: true,
          network,
          contractAddress: address,
          limit: 20,
        },
        headers: { "Cache-Control": "no-store" },
      });

      const list = Array.isArray(res?.data?.records)
        ? res.data.records
        : Array.isArray(res?.data?.actions)
          ? res.data.actions
          : [];

      setHistory([...list].sort(compareNewestFirst));
    } catch (err) {
      setHistory([]);
      setHistoryError(
        isNetworkOfflineError(err)
          ? "You appear to be offline. Reconnect and try again."
          : safeApiError(err, "Token action history is temporarily unavailable."),
      );
    } finally {
      setHistoryLoading(false);
    }
  }, [isAuthed, network, address]);


  const loadAccountUsage = useCallback(async () => {
    if (!isAuthed) {
      setAccountUsage(null);
      setAccountUsageError("");
      return;
    }

    setAccountUsageLoading(true);
    setAccountUsageError("");

    try {
      const res = await api.get("/account/usage", {
        headers: { "Cache-Control": "no-store" },
      });

      setAccountUsage(res.data || null);
    } catch (err) {
      setAccountUsage(null);
      setAccountUsageError(
        isNetworkOfflineError(err)
          ? "You appear to be offline. Reconnect and try again."
          : safeApiError(err, "Account usage is temporarily unavailable."),
      );
    } finally {
      setAccountUsageLoading(false);
    }
  }, [isAuthed]);

  const loadDashboardBundle = useCallback(
    async ({ force = false } = {}) => {
      if (!hasValidParams) return;
      if (!isAuthed) return;

      const key = `${activeWallet || ""}:${network}:${address}`;
      const now = Date.now();
      const last = dashboardLoadRef.current;
      const hasDashboardData = Boolean(overview || tokenReadInfo);

      if (
        !force &&
        hasDashboardData &&
        last.key === key &&
        now - last.at < 30000
      ) {
        return;
      }

      if (!force && last.inFlight && last.key === key) {
        return;
      }

      dashboardLoadRef.current = {
        key,
        at: now,
        inFlight: true,
      };

      try {
        await Promise.allSettled([
          loadOverview(force),
          loadHistory(),
          loadAccountUsage(),
        ]);
      } finally {
        dashboardLoadRef.current = {
          key,
          at: Date.now(),
          inFlight: false,
        };
      }
    },
    [
      hasValidParams,
      isAuthed,
      activeWallet,
      network,
      address,
      overview,
      tokenReadInfo,
      loadOverview,
      loadHistory,
      loadAccountUsage,
    ],
  );

  useEffect(() => {
    loadDashboardBundle({ force: false });
  }, [loadDashboardBundle]);

  const deployment = overview?.deployment || null;
  const token = overview?.token || null;

  const actionTokenInfo = useMemo(() => {
    return buildCustomTokenInfoFromOverview({
      overview: tokenReadInfo || overview || {},
      deployment,
      token,
    });
  }, [tokenReadInfo, overview, deployment, token]);

  const isCustomDeployment = isCustomTokenInfo(actionTokenInfo);
  const customActionCapabilities = getCustomActionCapabilities(actionTokenInfo);

  const analytics = overview?.analytics || null;
  const liquidity = overview?.liquidity || null;
  const permissions = overview?.permissions || null;
  const verification = overview?.verification || null;
  const refreshPolicy = overview?.refreshPolicy || null;

  const displayName = useMemo(() => {
    const name = String(token?.tokenName || deployment?.tokenName || "").trim();
    const symbol = String(
      token?.tokenSymbol || deployment?.tokenSymbol || "",
    ).trim();

    if (name && symbol) return `${name} (${symbol})`;
    if (name) return name;
    if (symbol) return symbol;

    return shorten(address);
  }, [token, deployment, address]);

  const dashboardContractType = useMemo(() => {
    return deriveDashboardContractType({
      deployment,
      token,
      tokenReadInfo,
    });
  }, [deployment, token, tokenReadInfo]);

  const tokenType = dashboardContractType.tokenType || "-";
  const isDashboardToken = Boolean(dashboardContractType.isToken);
  const dashboardTitle = isDashboardToken ? "Token Dashboard" : "Contract Dashboard";
  const dashboardSummaryTitle = isDashboardToken ? "Token summary" : "Contract summary";
  const dashboardDisplayType = dashboardContractType.displayType || tokenType || "Contract";

  const explorerUrl = String(deployment?.explorerUrl || "");
  const deployerAddress = String(deployment?.deployerAddress || "-");

  const deploymentTxHash = String(deployment?.txHash || "");
  const deploymentTxUrl = String(deployment?.explorerTxUrl || "");

  const modules = useMemo(() => {
    const candidates = [
      ...(Array.isArray(tokenReadInfo?.modules) ? tokenReadInfo.modules : []),
      ...(Array.isArray(tokenReadInfo?.deployment?.modules)
        ? tokenReadInfo.deployment.modules
        : []),
      ...(Array.isArray(deployment?.modules) ? deployment.modules : []),
      ...(Array.isArray(deployment?.token?.modules)
        ? deployment.token.modules
        : []),
    ];

    return Array.from(
      new Set(
        candidates
          .map((m) => String(m || "").trim().toLowerCase())
          .filter(Boolean),
      ),
    );
  }, [tokenReadInfo, deployment]);

  const normalizedModules = modules;

  const hasMintable =
    normalizedModules.includes("mintable") ||
    customActionCapabilities.mint === true ||
    customActionCapabilities.mintable === true;

  const hasBurnable =
    normalizedModules.includes("burnable") ||
    customActionCapabilities.burn === true ||
    customActionCapabilities.burnable === true;

  const hasPausable =
    normalizedModules.includes("pausable") ||
    customActionCapabilities.pause === true ||
    customActionCapabilities.unpause === true ||
    customActionCapabilities.pausable === true;

  const hasAccessControl =
    customActionCapabilities.accessControl === true ||
    customActionCapabilities.grantRole === true ||
    customActionCapabilities.revokeRole === true ||
    customActionCapabilities.renounceRole === true ||
    (!isCustomDeployment && (hasMintable || hasPausable));

  const availableRoleOptions = useMemo(() => {
    const options = ROLE_OPTIONS.filter((item) => {
      if (item.value === "MINTER_ROLE") return hasMintable;
      if (item.value === "PAUSER_ROLE") return hasPausable;
      return false;
    });

    return options.length ? options : ROLE_OPTIONS.slice(0, 1);
  }, [hasMintable, hasPausable]);

  const canShowRoleManagement =
    Boolean(hasAccessControl) && Boolean(availableRoleOptions.length);

  useEffect(() => {
    if (!availableRoleOptions.some((item) => item.value === roleName)) {
      setRoleName(availableRoleOptions[0]?.value || "MINTER_ROLE");
    }
  }, [availableRoleOptions, roleName]);

  const currentOwnerAddress = useMemo(() => {
    const candidates = [
      tokenReadInfo?.owner,
      tokenReadInfo?.ownerAddress,
      actionTokenInfo?.owner,
      actionTokenInfo?.ownerAddress,
      actionTokenInfo?.deployment?.ownerAddress,
      deployment?.ownerAddress,
    ];

    const found = candidates
      .map((value) => String(value || "").trim())
      .find((value) => isEvmAddress(value));

    return found || "";
  }, [tokenReadInfo, actionTokenInfo, deployment]);

  const canShowOwnershipControl =
    isCustomDeployment &&
    (
      customActionCapabilities.transferOwnership === true ||
      customActionCapabilities.ownable === true ||
      Boolean(currentOwnerAddress)
    );

  const liveOwnerAddress = useMemo(() => {
    const candidates = [
      tokenReadInfo?.owner,
      tokenReadInfo?.ownerAddress,
    ];

    const found = candidates
      .map((value) => String(value || "").trim())
      .find((value) => isEvmAddress(value));

    return found || "";
  }, [tokenReadInfo]);

  const isConnectedOwner = useMemo(() => {
    if (!activeWallet || !isEvmAddress(activeWallet)) return false;
    if (!currentOwnerAddress || !isEvmAddress(currentOwnerAddress)) return false;

    return (
      activeWallet.toLowerCase() === currentOwnerAddress.toLowerCase()
    );
  }, [activeWallet, currentOwnerAddress]);

  const ownershipAccessMessage = useMemo(() => {
    if (!canShowOwnershipControl) {
      return "This contract does not expose ownership transfer controls for this dashboard.";
    }

    if (!activeWallet || !isEvmAddress(activeWallet)) {
      return "Connect the current owner wallet to transfer ownership.";
    }

    if (!currentOwnerAddress || !isEvmAddress(currentOwnerAddress)) {
      return "Current owner is not available yet. Click Refresh to read the latest owner() value from the contract.";
    }

    if (!isConnectedOwner) {
      return `This connected wallet is not the current owner. Current owner is ${shorten(
        currentOwnerAddress,
      )}. This wallet can still view the contract, but owner-only controls are disabled. It can operate again only if ownership is transferred back or the contract grants this wallet a supported role.`;
    }

    return liveOwnerAddress
      ? "This wallet matches the live owner() value. Ownership actions are available."
      : "This wallet matches the saved owner record. Click Refresh if you want to re-check the live owner() value.";
  }, [
    canShowOwnershipControl,
    activeWallet,
    currentOwnerAddress,
    isConnectedOwner,
    liveOwnerAddress,
  ]);

  const ownershipInputDisabled =
    ownershipLoading || !isConnectedOwner;

  const canTransferOwnership =
    canShowOwnershipControl &&
    isConnectedOwner &&
    !ownershipLoading &&
    isEvmAddress(ownershipNewOwner) &&
    (
      !currentOwnerAddress ||
      ownershipNewOwner.toLowerCase() !== currentOwnerAddress.toLowerCase()
    );

  const verified = useMemo(() => {
    const vs = String(
      verification?.status || deployment?.verificationStatus || "",
    ).toLowerCase();
    return vs === "verified";
  }, [verification, deployment]);

  const verificationStatus = useMemo(() => {
    return String(
      verification?.status || deployment?.verificationStatus || "not_requested",
    );
  }, [verification, deployment]);

  const isAdmin = useMemo(() => {
    const possibleRoles = [
      accountUsage?.role,
      accountUsage?.user?.role,
      accountUsage?.account?.role,
      accountUsage?.membership?.role,
      accountUsage?.subscription?.role,
      accountUsage?.planRole,
      permissions?.role,
      permissions?.userRole,
      permissions?.accountRole,
      permissions?.membershipRole,
      overview?.role,
      overview?.user?.role,
    ]
      .map((value) => String(value || "").trim().toUpperCase())
      .filter(Boolean);

    const hasAdminRole = possibleRoles.some((role) =>
      ["ADMIN", "SUPER_ADMIN", "OWNER"].includes(role),
    );

    return (
      hasAdminRole ||
      permissions?.isAdmin === true ||
      accountUsage?.isAdmin === true ||
      overview?.isAdmin === true
    );
  }, [accountUsage, permissions, overview]);

  const effectivePlan = useMemo(() => {
    if (isAdmin) return "admin";

    const rawPlan =
      accountUsage?.plan ||
      accountUsage?.planCode ||
      accountUsage?.subscription?.plan ||
      permissions?.planCode ||
      "free";

    return normalizePlanCode(rawPlan);
  }, [accountUsage, permissions, isAdmin]);

  const hasResolvedProtectedContext =
    Boolean(isAuthed) &&
    !overviewLoading &&
    !accountUsageLoading &&
    !overviewError &&
    !accountUsageError;

  const hasAdvancedPlan = hasPaidFeatureAccess(effectivePlan);

  const canViewAdvancedAnalytics = useMemo(() => {
    if (!hasResolvedProtectedContext) return false;

    return (
      isAdmin ||
      hasAdvancedPlan ||
      Boolean(permissions?.canViewAdvancedAnalytics)
    );
  }, [hasResolvedProtectedContext, isAdmin, hasAdvancedPlan, permissions]);

  const canViewPremiumInsights = useMemo(() => {
    if (!hasResolvedProtectedContext) return false;

    return (
      isAdmin ||
      hasAdvancedPlan ||
      Boolean(permissions?.canViewPremiumInsights)
    );
  }, [hasResolvedProtectedContext, isAdmin, hasAdvancedPlan, permissions]);

  const canViewLiquidityInsights = canViewPremiumInsights;
  const isPaidPlan = isBillingPaidPlan(effectivePlan);

  const premiumSectionTitle = isPaidPlan
    ? "Paid Plan Access"
    : "Paid Plan Features";

  const premiumSectionSubtitle = isPaidPlan
    ? "Analytics and supported market or liquidity insights are enabled for this workspace where data is available."
    : "Upgrade to Starter or Pro to unlock holder trends, transfer activity, and supported market or liquidity metrics where data is available.";

  const holderTrend = useMemo(() => {
    if (Array.isArray(analytics?.holderSeries) && analytics.holderSeries.length) {
      return analytics.holderSeries;
    }

    const currentHolderCount = token?.holderCount ?? analytics?.holderCount;

    if (currentHolderCount != null) {
      return buildFallbackTrendSeries(currentHolderCount, 7);
    }

    return [];
  }, [analytics, token]);

  const transferTrend = useMemo(() => {
    if (
      Array.isArray(analytics?.transferSeries) &&
      analytics.transferSeries.length
    ) {
      return analytics.transferSeries;
    }

    const currentTransferCount =
      token?.transferCount24h ?? analytics?.transferCount24h;

    if (currentTransferCount != null) {
      return buildFallbackTrendSeries(currentTransferCount, 7);
    }

    return [];
  }, [analytics, token]);

  const usageSummary = useMemo(() => {
    const usage = accountUsage?.usage || accountUsage || {};
    const limits = accountUsage?.limits || {};

    return {
      plan: effectivePlan,
      deploymentsUsed: usage.deploymentsUsed ?? usage.deployments ?? 0,
      deploymentsLimit: isAdmin ? null : limits.deployments ?? null,
    };
  }, [accountUsage, effectivePlan, isAdmin]);

  const liquidityState = useMemo(() => {
    const syncStatus = String(liquidity?.syncStatus || "").toLowerCase();

    return {
      isTestnet: isTestnetLiquidityStatus(syncStatus),
      isNoPool: isNoPoolLiquidityStatus(syncStatus),
      isAvailable: isLiquidityAvailable(syncStatus),
      syncStatus,
    };
  }, [liquidity]);

  const recentTokenActions = useMemo(() => {
    const addrLower = toLower(address);
    const allowed = new Set([
      "mint",
      "burn",
      "pause",
      "unpause",
      "grantrole",
      "revokerole",
      "renouncerole",
      "transferownership",
    ]);

    return (history || [])
      .filter((r) => {
        const kind = String(
          r?.kind || r?.action || r?.actionType || r?.type || "",
        ).toLowerCase();

        return allowed.has(kind);
      })
      .filter((r) => {
        const recordNetwork = normalizeNetwork(r?.network);
        return !recordNetwork || recordNetwork === network;
      })
      .filter((r) => {
        const recordAddress = toLower(
          r?.contractAddress ||
          r?.address ||
          r?.tokenAddress ||
          r?.result?.contractAddress ||
          "",
        );

        return !recordAddress || recordAddress === addrLower;
      })
      .sort(compareNewestFirst)
      .slice(0, 10);
  }, [history, address, network]);

  const actionSummary = useMemo(() => {
    const actions = Array.isArray(history) ? history : [];

    const countKind = (target) =>
      actions.filter((item) => {
        const kind = String(
          item?.kind || item?.action || item?.actionType || item?.type || "",
        ).toLowerCase();

        return kind === target;
      }).length;

    const tokenIds = new Set(
      actions
        .map((item) => item?.tokenId ?? item?.id)
        .filter((value) => value != null && String(value).trim() !== "")
        .map((value) => String(value).trim()),
    );

    const lastAction = [...actions].sort(compareNewestFirst)[0] || null;

    return {
      total: actions.length,
      mints: countKind("mint"),
      burns: countKind("burn"),
      pauses: countKind("pause"),
      unpauses: countKind("unpause"),
      roleGrants: countKind("grantrole"),
      roleRevokes: countKind("revokerole"),
      roleRenounces: countKind("renouncerole"),
      ownershipTransfers: countKind("transferownership"),
      tokenIdsTouched: tokenIds.size,
      lastAction,
    };
  }, [history]);

  const isErc20Token = isDashboardToken && tokenType === "ERC20";
  const isNftToken = isDashboardToken && (tokenType === "ERC721" || tokenType === "ERC1155");

  const analyticsRefreshMode = String(
    refreshPolicy?.analyticsRefreshMode || "",
  ).toLowerCase();

  const isAnalyticsNotApplicable =
    analyticsRefreshMode === "not_applicable" || !isDashboardToken;

  const analyticsRefreshNotice = isAnalyticsNotApplicable
    ? refreshPolicy?.message ||
    "Token analytics and liquidity analytics are not applicable for this non-token custom contract."
    : refreshPolicy?.message ||
    "Analytics and liquidity use the latest saved snapshot. Click Refresh to fetch fresh data.";

  const loadWalletBalance = useCallback(async () => {
    setWalletBalanceError("");
    setWalletBalanceValue(null);

    if (!isAuthed) {
      setWalletBalanceError("Sign in to read wallet balance.");
      return;
    }

    if (!activeWallet || !isEvmAddress(activeWallet)) {
      setWalletBalanceError("Connect a wallet to read balance.");
      return;
    }

    if (!hasValidParams) {
      setWalletBalanceError("Invalid token network or address.");
      return;
    }

    if (!tokenType || tokenType === "-") {
      setWalletBalanceError("Token standard is not available yet.");
      return;
    }

    if (tokenType === "ERC1155" && !isIntegerString(walletBalanceTokenId)) {
      setWalletBalanceError("Enter an ERC1155 Token ID to read balance.");
      return;
    }

    setWalletBalanceLoading(true);

    try {
      const res = await api.post("/balance", {
        network,
        tokenType,
        contractAddress: address,
        wallet: activeWallet,
        id: tokenType === "ERC1155" ? walletBalanceTokenId.trim() : undefined,
      });

      const data = res.data || {};

      if (!data.ok) {
        throw new Error(data.error || "Balance check failed.");
      }

      setWalletBalanceValue(
        data.formattedBalance ??
        data.balance ??
        "0"
      );
    } catch (err) {
      setWalletBalanceError(
        isNetworkOfflineError(err)
          ? "You appear to be offline. Reconnect and try again."
          : safeApiError(err, "Unable to fetch wallet balance."),
      );
    } finally {
      setWalletBalanceLoading(false);
    }
  }, [
    isAuthed,
    activeWallet,
    hasValidParams,
    tokenType,
    walletBalanceTokenId,
    network,
    address,
  ]);

  useEffect(() => {
    if (!isAuthed) return;
    if (!activeWallet) return;
    if (!hasValidParams) return;
    if (tokenType === "ERC1155") return;
    if (!["ERC20", "ERC721"].includes(tokenType)) return;

    loadWalletBalance();
  }, [isAuthed, activeWallet, hasValidParams, tokenType, loadWalletBalance]);

  const royaltyMetadata = useMemo(() => {
    const deploymentToken = deployment?.token || {};

    const source =
      token?.royaltyFeeNumerator != null || token?.royaltyReceiver
        ? token
        : deploymentToken;

    let rawFee =
      source?.royaltyFeeNumerator ??
      source?.royaltyBps ??
      source?.royaltyFeeBps ??
      null;

    let receiver = String(source?.royaltyReceiver || "").trim();
    let sourceLabel = "deployment metadata";

    const constructorArgs = Array.isArray(deployment?.constructorArgs)
      ? deployment.constructorArgs
      : [];

    if ((rawFee == null || rawFee === "") && !receiver) {
      if (tokenType === "ERC721" && constructorArgs.length >= 6) {
        receiver = String(constructorArgs[4] || "").trim();
        rawFee = constructorArgs[5];
        sourceLabel = "deployment constructor args";
      }

      if (tokenType === "ERC1155" && constructorArgs.length >= 3) {
        receiver = String(constructorArgs[1] || "").trim();
        rawFee = constructorArgs[2];
        sourceLabel = "deployment constructor args";
      }
    }

    const feeNumber =
      rawFee == null || rawFee === "" ? null : Number(rawFee);

    const hasFee = Number.isFinite(feeNumber);
    const enabled = hasFee && feeNumber > 0;

    return {
      available: hasFee || Boolean(receiver),
      enabled,
      feeNumerator: hasFee ? feeNumber : null,
      percent: hasFee ? formatRoyaltyPercent(feeNumber) : "-",
      receiver: receiver || "-",
      sourceLabel,
    };
  }, [token, deployment, tokenType]);

  const loadNftInsights = useCallback(async () => {
    if (!isAuthed) {
      setNftInsights(null);
      setNftInsightsError("");
      return;
    }

    if (!isNftToken || !network || !isEvmAddress(address)) {
      setNftInsights(null);
      setNftInsightsError("");
      return;
    }

    setNftInsightsLoading(true);
    setNftInsightsError("");

    try {
      const res = await api.get("/nft-insights", {
        params: {
          network,
          address,
        },
        headers: { "Cache-Control": "no-store" },
      });

      setNftInsights(res.data || null);
    } catch (err) {
      setNftInsights(null);
      setNftInsightsError(
        isNetworkOfflineError(err)
          ? "You appear to be offline. Reconnect and try again."
          : safeApiError(err, "NFT indexed insights are temporarily unavailable."),
      );
    } finally {
      setNftInsightsLoading(false);
    }
  }, [isAuthed, isNftToken, network, address]);

  /*
    NFT indexed insights are intentionally manual-refresh only.
    This avoids calling external indexers automatically when the dashboard opens.
    Users can click Refresh to fetch fresh NFT analytics.
  */

  useEffect(() => {
    if (!hasValidParams) return;
    if (!isAuthed) return;

    let refreshTimer = null;

    const refreshDashboard = () => {
      if (refreshTimer) clearTimeout(refreshTimer);

      refreshTimer = setTimeout(() => {
        loadDashboardBundle({ force: false });
      }, 700);
    };

    window.addEventListener("online", refreshDashboard);

    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      window.removeEventListener("online", refreshDashboard);
    };
  }, [
    hasValidParams,
    isAuthed,
    loadDashboardBundle,
  ]);

  const marketplaceLinks = useMemo(() => {
    if (!isNftToken || !isEvmAddress(address)) return [];

    const chain = String(network || "").toLowerCase();
    const encodedAddress = encodeURIComponent(address);
    const links = [];

    const openSeaSlugByNetwork = {
      ethereum: "ethereum",
      polygon: "matic",
      base: "base",
      arbitrum: "arbitrum",
      optimism: "optimism",
      avalanche: "avalanche",
    };

    const openSeaSlug = openSeaSlugByNetwork[chain];

    if (openSeaSlug) {
      links.push({
        label: "OpenSea",
        url: `https://opensea.io/assets/${openSeaSlug}/${encodedAddress}`,
      });
    }

    if (explorerUrl) {
      links.push({
        label: "Explorer",
        url: explorerUrl,
      });
    }

    return links;
  }, [isNftToken, network, address, explorerUrl]);

  const actionsLink = useMemo(() => {
    return `/studio?network=${encodeURIComponent(
      network,
    )}&address=${encodeURIComponent(address)}`;
  }, [network, address]);

  const operationsLink = useMemo(() => {
    return `/operations?network=${encodeURIComponent(
      network,
    )}&address=${encodeURIComponent(address)}`;
  }, [network, address]);

  const helpCenterLink = useMemo(() => {
    const tokenNameValue = encodeURIComponent(
      String(token?.tokenName || deployment?.tokenName || "").trim(),
    );
    const tokenSymbolValue = encodeURIComponent(
      String(token?.tokenSymbol || deployment?.tokenSymbol || "").trim(),
    );
    const tokenTypeValue = encodeURIComponent(
      String(token?.tokenType || deployment?.tokenType || "").trim(),
    );
    const txHashValue = encodeURIComponent(String(deployment?.txHash || "").trim());

    return `/help-center?network=${encodeURIComponent(
      network,
    )}&address=${encodeURIComponent(address)}&tokenType=${tokenTypeValue}&tokenName=${tokenNameValue}&tokenSymbol=${tokenSymbolValue}&txHash=${txHashValue}`;
  }, [network, address, token, deployment]);

  const shouldShowInitialDashboardLoader =
    !overview &&
    !tokenReadInfo &&
    (overviewLoading || historyLoading || accountUsageLoading);

  const historyRoute = "/history";

  const openActionModal = useCallback((kind) => {
    setActionModal(kind);
    setActionAmount("");
    setActionTokenId("");
    setAcceptedActionRisk(false);
    setActionStatus("");
  }, []);

  const closeActionModal = useCallback(() => {
    if (actionLoading) return;
    setActionModal(null);
    setActionAmount("");
    setActionTokenId("");
    setAcceptedActionRisk(false);
    setActionStatus("");
  }, [actionLoading]);

  const handleTokenAction = useCallback(async () => {
    if (!actionModal) return;

    setActionStatus("");

    if (!acceptedActionRisk) {
      setActionStatus("Please accept the action terms before continuing.");
      return;
    }

    if (!activeWallet || !isEvmAddress(activeWallet)) {
      setActionStatus("Connect your wallet before continuing.");
      return;
    }

    if (!hasValidParams) {
      setActionStatus("Invalid token network or address.");
      return;
    }

    const kind = actionModal;
    const currentTokenType = tokenType;

    if (kind === "burn") {
      if (currentTokenType === "ERC20") {
        if (!isDecimalString(actionAmount)) {
          setActionStatus("Enter a valid ERC20 amount to burn.");
          return;
        }

        const burnAmount = Number(actionAmount);

        if (!Number.isFinite(burnAmount) || burnAmount <= 0) {
          setActionStatus("ERC20 burn amount must be greater than 0.");
          return;
        }

        if (burnAmount > MAX_BURN_ERC20_HUMAN_UI) {
          setActionStatus(
            `ERC20 burn amount is too large. Max per transaction is ${MAX_BURN_ERC20_HUMAN_UI.toLocaleString()} tokens.`,
          );
          return;
        }
      }

      if (
        (currentTokenType === "ERC721" || currentTokenType === "ERC1155") &&
        !isIntegerString(actionTokenId)
      ) {
        setActionStatus("Enter a valid token ID.");
        return;
      }

      if (currentTokenType === "ERC1155") {
        if (!isIntegerString(actionAmount)) {
          setActionStatus("Enter a valid ERC1155 quantity.");
          return;
        }

        const burnQty = Number(actionAmount);

        if (!Number.isInteger(burnQty) || burnQty <= 0) {
          setActionStatus("ERC1155 burn quantity must be greater than 0.");
          return;
        }

        if (burnQty > MAX_BURN_ERC1155_QTY_UI) {
          setActionStatus(
            `ERC1155 burn quantity is too large. Max per transaction is ${MAX_BURN_ERC1155_QTY_UI.toLocaleString()}.`,
          );
          return;
        }
      }
    }

    const endpoint =
      kind === "burn" ? "/burn" : kind === "pause" ? "/pause" : "/unpause";

    const confirmEndpoint =
      kind === "burn"
        ? "/burn/confirm"
        : kind === "pause"
          ? "/pause/confirm"
          : "/unpause/confirm";

    setActionLoading(true);

    try {
      setActionStatus(`Preparing ${kind} transaction…`);
      if (isCustomDeployment) {
        let customArgs = {};

        if (kind === "burn") {
          customArgs = buildCustomBurnArgs({
            tokenType: currentTokenType,
            from: activeWallet,
            tokenId: actionTokenId.trim(),
            amount: actionAmount.trim(),
            tokenInfo: actionTokenInfo,
          });
        }

        setActionStatus(`Preparing custom ${kind} transaction…`);

        const prep = await prepareCustomTokenAction({
          tokenInfo: actionTokenInfo,
          network,
          contractAddress: address,
          action: kind,
          args: customArgs,
        });

        setActionStatus("Switching wallet to the token network…");
        await ensureWalletOnChain(prep.chainId);

        setActionStatus(`Confirm custom ${kind} in your wallet…`);
        const txHash = await sendUserTransaction(
          prep.tx,
          activeWallet,
          prep.chainId,
        );

        setActionStatus(`${kind} tx sent (${shorten(txHash)}). Confirming…`);

        const confirmData = await confirmWithRetry(
          () =>
            confirmCustomTokenAction({
              actionRequestId: prep.actionRequestId,
              txHash,
              network,
              contractAddress: address,
              confirmations: 1,
            }),
          { attempts: 6, delayMs: 2000 },
        );

        const ok = Boolean(confirmData?.ok);
        setActionStatus(
          ok
            ? `Custom ${kind} confirmed ✅`
            : `Custom ${kind} submitted. Confirmation pending.`,
        );

        await Promise.allSettled([
          loadOverview({ refresh: false, tokenRefresh: true }),
          loadHistory(),
          loadAccountUsage(),
          ["ERC20", "ERC721"].includes(currentTokenType)
            ? loadWalletBalance()
            : Promise.resolve(),
        ]);

        return;
      }

      const prepPayload = {
        network,
        mode: "user",
        tokenType: currentTokenType,
        artifactKey: deployment?.artifactKey || token?.artifactKey || null,
        contractAddress: address,
        userAddress: activeWallet,
      };

      if (kind === "burn") {
        if (currentTokenType === "ERC20") {
          prepPayload.amount = actionAmount.trim();
          prepPayload.amountFormat = "human";
        } else if (currentTokenType === "ERC721") {
          prepPayload.tokenId = actionTokenId.trim();
        } else if (currentTokenType === "ERC1155") {
          prepPayload.id = actionTokenId.trim();
          prepPayload.tokenId = actionTokenId.trim();
          prepPayload.amount = actionAmount.trim();
        }
      }

      const prepRes = await api.post(endpoint, prepPayload);
      const prep = prepRes.data;

      if (!prep?.ok || !prep?.tx?.data) {
        throw new Error(prep?.message || prep?.error || `Backend did not return ${kind} tx.`);
      }

      setActionStatus("Switching wallet to the token network…");
      await ensureWalletOnChain(prep.chainId);

      setActionStatus(`Confirm ${kind} in your wallet…`);
      const txHash = await sendUserTransaction(prep.tx, activeWallet, prep.chainId);

      setActionStatus(`${kind} tx sent (${shorten(txHash)}). Confirming…`);

      const confirmPayload = {
        network,
        txHash,
        contractAddress: address,
        userAddress: activeWallet,
        tokenType: currentTokenType,
        tokenId: actionTokenId ? actionTokenId.trim() : null,
        id: actionTokenId ? actionTokenId.trim() : null,
        amount: actionAmount ? actionAmount.trim() : null,
        amountFormat: currentTokenType === "ERC20" ? "human" : undefined,
        confirmations: 1,
      };

      const confirmRes = await confirmWithRetry(
        () => api.post(confirmEndpoint, confirmPayload),
        { attempts: 6, delayMs: 2000 },
      );

      const ok = Boolean(confirmRes?.data?.ok);
      setActionStatus(ok ? `${kind} confirmed ✅` : `${kind} submitted. Confirmation pending.`);

      await Promise.allSettled([
        loadOverview({ refresh: false, tokenRefresh: true }),
        loadHistory(),
        loadAccountUsage(),
        ["ERC20", "ERC721"].includes(currentTokenType)
          ? loadWalletBalance()
          : Promise.resolve(),
      ]);
    } catch (err) {
      console.error(`[token-dashboard:${actionModal}] failed:`, err);
      setActionStatus(humanizeWalletActionError(err, `${actionModal} operation failed.`));
    } finally {
      setActionLoading(false);
    }
  }, [
    actionModal,
    acceptedActionRisk,
    activeWallet,
    hasValidParams,
    tokenType,
    actionAmount,
    actionTokenId,
    network,
    address,
    deployment,
    token,
    isCustomDeployment,
    actionTokenInfo,
    ensureWalletOnChain,
    sendUserTransaction,
    loadOverview,
    loadHistory,
    loadAccountUsage,
    loadWalletBalance,
  ]);

  const handleRoleAction = useCallback(async () => {
    const cleanKind = normalizeRoleKind(roleActionKind);
    const cleanRoleName = String(roleName || "").trim().toUpperCase();
    const cleanAccount = String(roleAccount || "").trim();

    setRoleStatus("");

    if (!activeWallet || !isEvmAddress(activeWallet)) {
      setRoleStatus("Connect your wallet before changing roles.");
      return;
    }

    if (!hasValidParams) {
      setRoleStatus("Invalid token network or address.");
      return;
    }

    if (!cleanRoleName) {
      setRoleStatus("Select a role.");
      return;
    }

    if (!isEvmAddress(cleanAccount)) {
      setRoleStatus("Enter a valid wallet address.");
      return;
    }

    if (!canShowRoleManagement) {
      setRoleStatus("This contract does not expose role management for this dashboard.");
      return;
    }

    setRoleLoading(true);

    try {
      const actionLabel =
        cleanKind === "grantRole" ? "grant role" : "revoke role";

      setRoleStatus(`Preparing ${actionLabel} transaction…`);

      if (isCustomDeployment) {
        const prep = await prepareCustomTokenAction({
          tokenInfo: actionTokenInfo,
          network,
          contractAddress: address,
          action: cleanKind,
          args: {
            roleName: cleanRoleName,
            account: cleanAccount,
          },
        });

        setRoleStatus("Switching wallet to the token network…");
        await ensureWalletOnChain(prep.chainId);

        setRoleStatus(`Confirm ${actionLabel} in your wallet…`);
        const txHash = await sendUserTransaction(
          prep.tx,
          activeWallet,
          prep.chainId,
        );

        setRoleStatus(`Role tx sent (${shorten(txHash)}). Confirming…`);

        const confirmData = await confirmWithRetry(
          () =>
            confirmCustomTokenAction({
              actionRequestId: prep.actionRequestId,
              txHash,
              network,
              contractAddress: address,
              confirmations: 1,
            }),
          { attempts: 6, delayMs: 2000 },
        );

        const ok = Boolean(confirmData?.ok);
        setRoleStatus(
          ok
            ? `${cleanRoleName} ${cleanKind === "grantRole" ? "granted" : "revoked"} ✅`
            : "Role transaction submitted. Confirmation pending.",
        );
      } else {
        const prepRes = await api.post("/role/prepare", {
          network,
          mode: "user",
          contractAddress: address,
          artifactKey: deployment?.artifactKey || token?.artifactKey || null,
          userAddress: activeWallet,
          action: cleanKind,
          roleName: cleanRoleName,
          account: cleanAccount,
        });

        const prep = prepRes.data;

        if (!prep?.ok || !prep?.tx?.data) {
          throw new Error(
            prep?.message ||
            prep?.error ||
            "Backend did not return a valid role transaction.",
          );
        }

        setRoleStatus("Switching wallet to the token network…");
        await ensureWalletOnChain(prep.chainId);

        setRoleStatus(`Confirm ${actionLabel} in your wallet…`);
        const txHash = await sendUserTransaction(
          prep.tx,
          activeWallet,
          prep.chainId,
        );

        setRoleStatus(`Role tx sent (${shorten(txHash)}). Confirming…`);

        const confirmRes = await confirmWithRetry(
          () =>
            api.post("/role/confirm", {
              network,
              contractAddress: address,
              txHash,
              userAddress: activeWallet,
              kind: cleanKind,
              action: cleanKind,
              roleName: cleanRoleName,
              role: prep.roleAction?.role,
              account: cleanAccount,
              tokenType,
              confirmations: 1,
            }),
          { attempts: 6, delayMs: 2000 },
        );

        const ok = Boolean(confirmRes?.data?.ok);
        setRoleStatus(
          ok
            ? `${cleanRoleName} ${cleanKind === "grantRole" ? "granted" : "revoked"} ✅`
            : "Role transaction submitted. Confirmation pending.",
        );
      }

      await Promise.allSettled([
        loadOverview({ refresh: false, tokenRefresh: true }),
        loadHistory(),
        loadAccountUsage(),
      ]);
    } catch (err) {
      console.error("[token-dashboard:role-action] failed:", err);
      setRoleStatus(humanizeWalletActionError(err, "Role action failed."));
    } finally {
      setRoleLoading(false);
    }
  }, [
    roleActionKind,
    roleName,
    roleAccount,
    activeWallet,
    hasValidParams,
    canShowRoleManagement,
    isCustomDeployment,
    actionTokenInfo,
    network,
    address,
    deployment,
    token,
    ensureWalletOnChain,
    sendUserTransaction,
    tokenType,
    loadOverview,
    loadHistory,
    loadAccountUsage,
  ]);
  const handleTransferOwnership = useCallback(async () => {
    const cleanNewOwner = String(ownershipNewOwner || "").trim();

    setOwnershipStatus("");

    if (!isCustomDeployment) {
      setOwnershipStatus("Ownership transfer is only available for custom Ownable contracts.");
      return;
    }

    if (!activeWallet || !isEvmAddress(activeWallet)) {
      setOwnershipStatus("Connect your wallet before transferring ownership.");
      return;
    }

    if (!hasValidParams) {
      setOwnershipStatus("Invalid token network or address.");
      return;
    }

    if (!currentOwnerAddress || !isEvmAddress(currentOwnerAddress)) {
      setOwnershipStatus(
        "Current owner is not available yet. Click Refresh to read the latest owner() value before transferring ownership.",
      );
      return;
    }

    if (!isConnectedOwner) {
      setOwnershipStatus(
        `This connected wallet is not the current owner. Current owner is ${shorten(
          currentOwnerAddress,
        )}. Owner-only controls are disabled for this wallet. It can operate again only if ownership is transferred back or the contract grants this wallet a supported role.`,
      );
      return;
    }

    if (!isEvmAddress(cleanNewOwner)) {
      setOwnershipStatus("Enter a valid new owner wallet address.");
      return;
    }

    if (
      currentOwnerAddress &&
      cleanNewOwner.toLowerCase() === currentOwnerAddress.toLowerCase()
    ) {
      setOwnershipStatus("New owner is already the current owner.");
      return;
    }

    setOwnershipLoading(true);

    try {
      setOwnershipStatus("Preparing transferOwnership transaction…");

      const prep = await prepareCustomTokenAction({
        tokenInfo: actionTokenInfo,
        network,
        contractAddress: address,
        action: "transferOwnership",
        args: {
          newOwner: cleanNewOwner,
        },
      });

      setOwnershipStatus("Switching wallet to the token network…");
      await ensureWalletOnChain(prep.chainId);

      setOwnershipStatus("Confirm ownership transfer in your wallet…");
      const txHash = await sendUserTransaction(
        prep.tx,
        activeWallet,
        prep.chainId,
      );

      setOwnershipStatus(`Ownership tx sent (${shorten(txHash)}). Confirming…`);

      const confirmData = await confirmWithRetry(
        () =>
          confirmCustomTokenAction({
            actionRequestId: prep.actionRequestId,
            txHash,
            network,
            contractAddress: address,
            confirmations: 1,
          }),
        { attempts: 6, delayMs: 2000 },
      );

      const ok = Boolean(confirmData?.ok);

      if (ok) {
        setOwnershipNewOwner("");
        setOwnershipStatus(
          `Ownership transferred and confirmed on-chain. Current owner is now ${shorten(
            cleanNewOwner,
          )}. This connected wallet can still view the contract, but owner-only controls are now disabled unless ownership is transferred back or this wallet receives a supported role.`,
        );
      } else {
        setOwnershipStatus("Ownership transfer submitted. Confirmation pending.");
      }

      await Promise.allSettled([
        loadOverview({ refresh: false, tokenRefresh: true }),
        loadHistory(),
        loadAccountUsage(),
      ]);
    } catch (err) {
      console.error("[token-dashboard:transfer-ownership] failed:", err);
      setOwnershipStatus(
        humanizeWalletActionError(err, "Ownership transfer failed."),
      );
    } finally {
      setOwnershipLoading(false);
    }
  }, [
    ownershipNewOwner,
    isCustomDeployment,
    activeWallet,
    hasValidParams,
    currentOwnerAddress,
    isConnectedOwner,
    actionTokenInfo,
    network,
    address,
    ensureWalletOnChain,
    sendUserTransaction,
    loadOverview,
    loadHistory,
    loadAccountUsage,
  ]);

  return !authReady ? (
    <div className="flex min-h-screen items-center justify-center bg-[#F7F8FA] px-4 py-8">
      <div className="rounded-3xl border border-slate-200 bg-white p-6 text-center text-sm font-medium text-slate-700 shadow-[0_18px_55px_rgba(15,23,42,0.06)]">
        Checking session…
      </div>
    </div>
  ) : !isAuthed ? (
    <div className="flex min-h-screen items-center justify-center bg-[#F7F8FA] px-4 py-8">
      <div className="max-w-md rounded-3xl border border-[#E7D8AC] bg-[#FFF9EA] p-6 text-center shadow-[0_18px_55px_rgba(15,23,42,0.06)]">
        <div className="text-sm font-semibold text-[#6F5318]">
          Sign in required
        </div>
        <p className="mt-2 text-sm leading-6 text-[#7A5A17]">
          Use the top header to connect and sign in before viewing this dashboard.
        </p>
      </div>
    </div>
  ) : shouldShowInitialDashboardLoader ? (
    <div className="flex min-h-screen items-center justify-center bg-[#F7F8FA] px-4 py-8">
      <div className="rounded-3xl border border-slate-200 bg-white p-6 text-center text-sm font-medium text-slate-700 shadow-[0_18px_55px_rgba(15,23,42,0.06)]">
        Loading contract dashboard…
      </div>
    </div>
  ) : (
    <div className="min-h-screen bg-[#F7F8FA] px-4 py-8 text-slate-950">
      <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,_rgba(197,155,69,0.10),_transparent_30%),radial-gradient(circle_at_top_right,_rgba(5,7,19,0.08),_transparent_30%),linear-gradient(to_bottom,_#F7F8FA,_#F2F3F5_45%,_#F7F8FA)]" />

      <div className="mx-auto max-w-6xl space-y-6">
        <div className="relative overflow-hidden rounded-[32px] border border-[#2A2418] bg-[#050713] p-6 text-white shadow-[0_24px_70px_rgba(5,7,19,0.28)] sm:p-8">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(197,155,69,0.24),_transparent_30%),radial-gradient(circle_at_bottom_right,_rgba(255,255,255,0.08),_transparent_32%)]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050713] via-[#050713]/95 to-[#111827]/90" />
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#E6C978]/60 to-transparent" />
          <div className="relative">
            <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
              <div className="max-w-3xl">
                <div className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.06] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#E6C978]">
                  {dashboardTitle}
                </div>

                <h1 className="mt-4 text-3xl font-semibold tracking-tight text-white sm:text-4xl">
                  {displayName}
                </h1>

                <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">
                  {isDashboardToken
                    ? "Monitor deployment metadata, token actions, analytics, liquidity state, and workspace access for "
                    : "Monitor deployment metadata, verification state, ownership, roles, and workspace access for "}
                  <span className="font-semibold text-white">{network || "-"}</span>.
                </p>

                <div className="mt-5 flex flex-wrap gap-2">
                  <Badge tone="brand">{dashboardDisplayType}</Badge>
                  <Badge tone={verified ? "success" : "warning"}>
                    {verified ? "Verified" : "Unverified"}
                  </Badge>
                  {permissions?.isAuthed ? (
                    <Badge tone="neutral">{isAdmin ? "Admin View" : "Mine"}</Badge>
                  ) : null}
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => {
                    try {
                      if (activeHistoryTokenKey) {
                        sessionStorage.removeItem(activeHistoryTokenKey);
                      }
                    } catch {
                      // ignore storage errors
                    }

                    navigate(historyRoute);
                  }}
                  className="inline-flex items-center justify-center rounded-2xl border border-white/15 bg-white/[0.06] px-4 py-3 text-sm font-semibold text-white transition hover:border-[#E6C978]/60 hover:bg-white/10"
                >
                  Back to History
                </button>

                <Link
                  to={actionsLink}
                  className="inline-flex items-center justify-center rounded-2xl border border-white/15 bg-white/[0.06] px-4 py-3 text-sm font-semibold text-white transition hover:border-[#E6C978]/60 hover:bg-white/10"
                  title="Open Studio with token pre-filled"
                >
                  Manage in Studio
                </Link>

                <Link
                  to={helpCenterLink}
                  className="inline-flex items-center justify-center rounded-2xl border border-white/15 bg-white/[0.06] px-4 py-3 text-sm font-semibold text-white transition hover:border-[#E6C978]/60 hover:bg-white/10"
                  title="Contact support with this contract context"
                >
                  Contact Support
                </Link>

                <button
                  onClick={() => {
                    loadOverview(true);
                    loadHistory();
                    loadAccountUsage();

                    if (isDashboardToken) {
                      loadWalletBalance();
                    }

                    if (isNftToken) {
                      loadNftInsights();
                    }
                  }}
                  className="inline-flex items-center justify-center rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-slate-950 transition hover:bg-[#FFF9EA] disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={!hasValidParams}
                  title={
                    !hasValidParams
                      ? "Invalid network/address in URL."
                      : isDashboardToken
                        ? "Refresh dashboard metadata and fetch fresh token analytics where supported."
                        : "Refresh contract metadata, ownership, roles, history, and usage. Token analytics are not applicable for this contract."
                  }
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>

        {!hasValidParams ? (
          <InlineNotice
            title="Invalid token route"
            subtitle="Expected route format: /token/:network/:address"
            tone="error"
          />
        ) : null}

        <SectionCard
          title={dashboardSummaryTitle}
          subtitle="Contract identity, explorer links, verification state, and normalized metadata."
          eyebrow="Overview"
        >
          {overviewLoading ? (
            <InlineNotice
              title="Loading contract overview"
              subtitle="Fetching contract metadata and backend-normalized token context."
            />
          ) : overviewError ? (
            <InlineNotice
              title="Unable to load overview"
              subtitle={overviewError}
              tone="error"
            />
          ) : (
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
              <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-6 shadow-sm">
                <div className="text-xl font-semibold tracking-tight text-slate-950">{displayName}</div>

                <div className="mt-3 space-y-2 text-sm">
                  <div>
                    <span className="text-slate-500">Contract:</span>{" "}
                    <span className="font-mono break-all text-slate-900">
                      {address}
                    </span>
                  </div>

                  {explorerUrl ? (
                    <a
                      href={explorerUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex rounded-full border border-[#E7D8AC] bg-white px-3 py-1.5 text-xs font-semibold text-[#7A5A17] transition hover:bg-[#FFF9EA]"
                    >
                      View contract on explorer
                    </a>
                  ) : null}
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                <MetricCard
                  label={isDashboardToken ? "Token Standard" : "Contract Type"}
                  value={dashboardDisplayType}
                  hint={
                    isDashboardToken
                      ? "Detected from deployment and token metadata."
                      : "Detected from custom deployment metadata and compiled ABI capabilities."
                  }
                  tone="brand"
                />
                <MetricCard
                  label="Verification"
                  value={verified ? "Verified" : "Unverified"}
                  hint="Deployment-backed verification state."
                  tone={verified ? "success" : "warning"}
                />
                {isNftToken ? (
                  <MetricCard
                    label="Royalties"
                    value={
                      royaltyMetadata.available
                        ? royaltyMetadata.percent
                        : "Not set"
                    }
                    hint={
                      royaltyMetadata.available
                        ? `Read from ${royaltyMetadata.sourceLabel}.`
                        : "No royalty metadata recorded for this deployment."
                    }
                    tone={royaltyMetadata.enabled ? "success" : "neutral"}
                  />
                ) : null}
              </div>
            </div>
          )}
        </SectionCard>

        <SectionCard
          title={isDashboardToken ? "Token actions" : "Custom contract support"}
          subtitle={
            isDashboardToken
              ? "Run available token actions through your connected wallet."
              : "This custom contract is deployed and verified through Krionex. Specialized business dashboards for every custom contract type are not included yet."
          }
          eyebrow="Operations"
          right={
            isDashboardToken ? (
              <Link
                to={operationsLink}
                className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-[#E7D8AC] hover:bg-[#FFF9EA] hover:text-slate-950"
              >
                Open Operations
              </Link>
            ) : null
          }
        >
          {isDashboardToken ? (
            <>
              <div className="grid gap-4 md:grid-cols-3">
                <Link
                  to={operationsLink}
                  className={cn(
                    "group rounded-2xl border p-5 shadow-sm transition",
                    hasMintable
                      ? "border-[#E7D8AC] bg-[#FFF9EA] hover:-translate-y-0.5 hover:shadow-[0_18px_45px_rgba(197,155,69,0.14)]"
                      : "pointer-events-none border-slate-200 bg-slate-50 opacity-60",
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">Mint</div>
                      <p className="mt-2 text-xs leading-5 text-slate-500">
                        {hasMintable
                          ? "Mint additional supply or token assets when the mintable module is enabled."
                          : "Minting was not enabled for this deployment."}
                      </p>
                    </div>
                    <Badge tone={hasMintable ? "success" : "neutral"}>
                      {hasMintable ? "Enabled" : "Off"}
                    </Badge>
                  </div>

                  {hasMintable ? (
                    <div className="mt-5 inline-flex rounded-xl bg-[#050713] px-3 py-2 text-xs font-semibold text-white shadow-[0_10px_25px_rgba(5,7,19,0.18)]">
                      Open Mint
                    </div>
                  ) : null}
                </Link>

                <button
                  type="button"
                  onClick={() => hasBurnable && openActionModal("burn")}
                  disabled={!hasBurnable}
                  className={cn(
                    "group rounded-2xl border p-5 text-left shadow-sm transition",
                    hasBurnable
                      ? "border-[#E7D8AC] bg-[#FFF9EA] hover:-translate-y-0.5 hover:shadow-[0_18px_45px_rgba(197,155,69,0.14)]"
                      : "cursor-not-allowed border-slate-200 bg-slate-50 opacity-60",
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">Burn</div>
                      <p className="mt-2 text-xs leading-5 text-slate-500">
                        {hasBurnable
                          ? "Burn tokens from the connected wallet balance."
                          : "Burning was not enabled for this deployment."}
                      </p>
                    </div>
                    <Badge tone={hasBurnable ? "warning" : "neutral"}>
                      {hasBurnable ? "Enabled" : "Off"}
                    </Badge>
                  </div>

                  {hasBurnable ? (
                    <div className="mt-5 inline-flex rounded-xl bg-[#8A6A1F] px-3 py-2 text-xs font-semibold text-white shadow-[0_10px_25px_rgba(197,155,69,0.20)]">
                      Burn Token
                    </div>
                  ) : null}
                </button>

                <div
                  className={cn(
                    "rounded-2xl border p-5 shadow-sm",
                    hasPausable
                      ? "border-[#E7D8AC] bg-[#FFF9EA]"
                      : "border-slate-200 bg-slate-50 opacity-60",
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">
                        Pause Control
                      </div>
                      <p className="mt-2 text-xs leading-5 text-slate-500">
                        {hasPausable
                          ? "Pause or unpause token operations. Owner wallet only."
                          : "Pausable module was not enabled for this deployment."}
                      </p>
                    </div>
                    <Badge tone={hasPausable ? "brand" : "neutral"}>
                      {hasPausable ? "Enabled" : "Off"}
                    </Badge>
                  </div>

                  <div className="mt-5 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => hasPausable && openActionModal("pause")}
                      disabled={!hasPausable}
                      className="inline-flex rounded-xl bg-[#050713] px-3 py-2 text-xs font-semibold text-white shadow-[0_10px_25px_rgba(5,7,19,0.18)] transition hover:bg-[#111827] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Pause
                    </button>

                    <button
                      type="button"
                      onClick={() => hasPausable && openActionModal("unpause")}
                      disabled={!hasPausable}
                      className="inline-flex rounded-xl border border-[#E7D8AC] bg-white px-3 py-2 text-xs font-semibold text-[#7A5A17] transition hover:bg-[#FFF9EA] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Unpause
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-5 rounded-2xl border border-slate-200 bg-white/75 p-4 text-xs leading-5 text-slate-500">
                Krionex only stores confirmed on-chain actions such as mint, burn,
                pause, unpause, role grants, and role revokes. Dashboard views,
                modal opens, cancelled wallet prompts, and normal UI clicks are not stored.
              </div>
            </>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              <InlineNotice
                title="Supported now"
                subtitle="Deployment, verification, explorer links, ownership control when owner()/transferOwnership() exist, and role management when AccessControl-compatible functions exist."
                tone="success"
              />

              <InlineNotice
                title="Not included yet"
                subtitle="Generic ABI read/write controls and specialized dashboards for staking, vesting, escrow, DAO, marketplace, airdrop, game, factory, or every custom business function."
                tone="warning"
              />
            </div>
          )}
        </SectionCard>

        <SectionCard
          title="Role management"
          subtitle="Grant or revoke operational roles after deployment. The connected wallet must hold DEFAULT_ADMIN_ROLE for standard AccessControl contracts."
          eyebrow="Access Control"
          right={
            <Badge tone={canShowRoleManagement ? "success" : "neutral"}>
              {canShowRoleManagement ? "Available" : "Unavailable"}
            </Badge>
          }
        >
          {canShowRoleManagement ? (
            <div className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <div className="text-sm font-semibold text-slate-950">
                  Supported roles
                </div>

                <div className="mt-4 space-y-3">
                  {availableRoleOptions.map((item) => (
                    <div
                      key={item.value}
                      className="rounded-2xl border border-white bg-white p-4 shadow-sm"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-slate-900">
                            {item.label}
                          </div>
                          <div className="mt-1 text-xs leading-5 text-slate-500">
                            {item.description}
                          </div>
                        </div>
                        <Badge tone="brand">{item.value}</Badge>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 rounded-2xl border border-[#E7D8AC] bg-[#FFF9EA] p-4 text-xs leading-5 text-[#6F5318]">
                  For safety, admin handover and DEFAULT_ADMIN_ROLE changes are
                  not shown here yet. First use this panel for MINTER_ROLE and
                  PAUSER_ROLE only.
                </div>
              </div>

              <div className="rounded-2xl border border-[#E7D8AC] bg-[#FFF9EA] p-5 shadow-sm">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-950">
                      Change role
                    </div>
                    <p className="mt-1 text-xs leading-5 text-slate-600">
                      The backend prepares the transaction, your wallet signs it,
                      then Krionex confirms the final on-chain role state.
                    </p>
                  </div>
                  <Badge tone={isCustomDeployment ? "warning" : "success"}>
                    {isCustomDeployment ? "Custom" : "Standard"}
                  </Badge>
                </div>

                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                      Action
                    </label>
                    <select
                      value={roleActionKind}
                      onChange={(event) => setRoleActionKind(event.target.value)}
                      disabled={roleLoading}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 outline-none transition focus:border-[#C59B45] focus:ring-4 focus:ring-[#E6C978]/20 disabled:opacity-60"
                    >
                      <option value="grantRole">Grant role</option>
                      <option value="revokeRole">Revoke role</option>
                    </select>
                  </div>

                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                      Role
                    </label>
                    <select
                      value={roleName}
                      onChange={(event) => setRoleName(event.target.value)}
                      disabled={roleLoading}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 outline-none transition focus:border-[#C59B45] focus:ring-4 focus:ring-[#E6C978]/20 disabled:opacity-60"
                    >
                      {availableRoleOptions.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label} — {item.value}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Target wallet address
                  </label>
                  <input
                    value={roleAccount}
                    onChange={(event) => setRoleAccount(event.target.value)}
                    disabled={roleLoading}
                    placeholder="0x..."
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#C59B45] focus:ring-4 focus:ring-[#E6C978]/20 disabled:opacity-60"
                  />
                </div>

                <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <button
                    type="button"
                    onClick={handleRoleAction}
                    disabled={
                      roleLoading ||
                      !activeWallet ||
                      !isEvmAddress(roleAccount)
                    }
                    className="inline-flex items-center justify-center rounded-2xl bg-[#050713] px-5 py-3 text-sm font-semibold text-white shadow-[0_16px_35px_rgba(5,7,19,0.22)] transition hover:-translate-y-0.5 hover:bg-[#111827] disabled:cursor-not-allowed disabled:translate-y-0 disabled:opacity-50"
                  >
                    {roleLoading
                      ? "Waiting for wallet..."
                      : roleActionKind === "grantRole"
                        ? "Grant Role"
                        : "Revoke Role"}
                  </button>

                  {roleAccount ? (
                    <div className="text-xs font-medium text-slate-500">
                      Target: {shorten(roleAccount)}
                    </div>
                  ) : null}
                </div>

                {roleStatus ? (
                  <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs leading-5 text-slate-700">
                    {roleStatus}
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <InlineNotice
              title="Role management is not available for this contract"
              subtitle="Deploy with mintable or pausable AccessControl modules, or use a compatible custom contract exposing grantRole and revokeRole."
              tone="neutral"
            />
          )}
        </SectionCard>

        {isCustomDeployment ? (
          <SectionCard
            title="Ownership control"
            subtitle="Transfer ownership for compatible custom Ownable contracts. This is separate from standard AccessControl roles."
            eyebrow="Custom Ownable"
            right={
              <Badge tone={canShowOwnershipControl ? "warning" : "neutral"}>
                {canShowOwnershipControl ? "Available" : "Unavailable"}
              </Badge>
            }
          >
            {canShowOwnershipControl ? (
              <div className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                  <div className="text-sm font-semibold text-slate-950">
                    Current owner
                  </div>

                  <div className="mt-3 break-all rounded-2xl border border-white bg-white p-4 text-sm font-semibold text-slate-900 shadow-sm">
                    {currentOwnerAddress || "Owner not available"}
                  </div>

                  <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs leading-5 text-amber-900">
                    Ownership transfer changes who controls owner-only functions. Only
                    transfer to a wallet you control or fully trust.
                  </div>
                  <div className="mt-4">
                    <InlineNotice
                      title={isConnectedOwner ? "Owner wallet connected" : "Ownership actions disabled"}
                      subtitle={ownershipAccessMessage}
                      tone={isConnectedOwner ? "success" : "warning"}
                    />
                  </div>

                  <div className="mt-3 text-xs leading-5 text-slate-500">
                    Owner proof: {liveOwnerAddress ? "Live owner() read from contract" : "Saved owner record. Click Refresh to re-check live owner()."}
                  </div>
                </div>

                <div className="rounded-2xl border border-[#E7D8AC] bg-[#FFF9EA] p-5 shadow-sm">
                  <div className="text-sm font-semibold text-slate-950">
                    Transfer ownership
                  </div>

                  <p className="mt-1 text-xs leading-5 text-slate-600">
                    Krionex prepares transferOwnership, your wallet signs it, and the
                    backend confirms the live owner() value after mining.
                  </p>

                  <div className="mt-5">
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                      New owner wallet address
                    </label>
                    <input
                      value={ownershipNewOwner}
                      onChange={(event) => setOwnershipNewOwner(event.target.value)}
                      disabled={ownershipInputDisabled}
                      placeholder="0x..."
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#C59B45] focus:ring-4 focus:ring-[#E6C978]/20 disabled:opacity-60"
                    />
                  </div>

                  <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <button
                      type="button"
                      onClick={handleTransferOwnership}
                      disabled={!canTransferOwnership}
                      title={
                        !isConnectedOwner
                          ? ownershipAccessMessage
                          : !isEvmAddress(ownershipNewOwner)
                            ? "Enter a valid new owner wallet address."
                            : "Transfer ownership to the new owner wallet."
                      }
                      className="inline-flex items-center justify-center rounded-2xl bg-[#050713] px-5 py-3 text-sm font-semibold text-white shadow-[0_16px_35px_rgba(5,7,19,0.22)] transition hover:-translate-y-0.5 hover:bg-[#111827] disabled:cursor-not-allowed disabled:translate-y-0 disabled:opacity-50"
                    >
                      {ownershipLoading ? "Waiting for wallet..." : "Transfer Ownership"}
                    </button>

                    {ownershipNewOwner ? (
                      <div className="text-xs font-medium text-slate-500">
                        New owner: {shorten(ownershipNewOwner)}
                      </div>
                    ) : null}
                  </div>

                  {ownershipStatus ? (
                    <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs leading-5 text-slate-700">
                      {ownershipStatus}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <InlineNotice
                title="Ownership transfer is not available"
                subtitle="This custom contract must expose owner() and transferOwnership(address)."
                tone="neutral"
              />
            )}
          </SectionCard>
        ) : null}

        <SectionCard
          title="Account usage"
          subtitle="Current plan, deployment allowance, preflight allowance, and feature access for this workspace."
          eyebrow="Workspace"
          right={
            !isAdmin && usageSummary.plan === "free" ? (
              <Link
                to="/upgrade"
                className="inline-flex items-center justify-center rounded-2xl bg-[#050713] px-4 py-3 text-sm font-semibold text-white shadow-[0_16px_35px_rgba(5,7,19,0.20)] transition hover:-translate-y-0.5 hover:bg-[#111827]"
              >
                Upgrade Plan
              </Link>
            ) : null
          }
        >
          {!isAuthed ? (
            <InlineNotice
              title="Sign in to view usage"
              subtitle="See plan access, deployment usage, and upgrade availability after authentication."
              tone="warning"
            />
          ) : accountUsageLoading ? (
            <InlineNotice
              title="Loading account usage"
              subtitle="Fetching current plan and deployment allowance."
            />
          ) : accountUsageError ? (
            <InlineNotice
              title="Unable to load account usage"
              subtitle={accountUsageError}
              tone="error"
            />
          ) : (
            <div className="grid gap-4 md:grid-cols-3">
              <MetricCard
                label={isAdmin ? "Access Level" : "Current Plan"}
                value={isAdmin ? "ADMIN" : String(usageSummary.plan).toUpperCase()}
                hint={
                  isAdmin
                    ? "Administrative access override for this workspace."
                    : "Current SaaS entitlement for this wallet."
                }
                tone="brand"
              />
              <MetricCard
                label="Deployments Used"
                value={formatUsageStat(
                  usageSummary.deploymentsUsed,
                  usageSummary.deploymentsLimit,
                )}
                hint="Includes automatic contract verification."
                tone="neutral"
              />
              <MetricCard
                label="Analytics Access"
                value={isAdmin ? "Admin Override" : canViewAdvancedAnalytics ? "Advanced" : "Basic"}
                hint={
                  isAdmin
                    ? "Administrative access includes advanced analytics and Market & Liquidity Insights."
                    : "Market & Liquidity Insights and advanced analytics depend on plan."
                }
                tone={canViewAdvancedAnalytics ? "success" : "warning"}
              />
            </div>
          )}
        </SectionCard>

        {isDashboardToken ? (
          <SectionCard
            title={isNftToken ? "Token overview metrics" : "Analytics overview"}
            subtitle={
              isNftToken
                ? "Basic token metrics and indexed activity where available. Click Refresh to fetch fresh indexed data."
                : "Token metrics, indexed activity, and market context where data is available. Click Refresh to fetch fresh analytics."
            }
            eyebrow="Metrics"
          >
            <div className="mb-4">
              <InlineNotice
                title="Manual analytics refresh"
                subtitle={analyticsRefreshNotice}
                tone="neutral"
              />
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4 text-sm">
              <MetricCard
                label="Total Supply"
                value={formatLargeMetricValue(token?.totalSupply ?? analytics?.totalSupply)}
                hint="Latest saved token supply snapshot. Click Refresh to fetch fresh analytics where supported."
                tone="neutral"
                compactValue
                valueClassName="font-sans font-normal text-sm sm:text-base md:text-lg tracking-normal leading-snug"
              />

              <MetricCard
                label="Decimals"
                value={formatNumber(token?.decimals ?? analytics?.decimals)}
                hint="Token precision for display and transfers."
                tone="neutral"
              />

              <MetricCard
                label="Holders"
                value={
                  canViewAdvancedAnalytics
                    ? formatNumber(
                      token?.holderCount ?? analytics?.holderCount,
                      "No holder data yet",
                    )
                    : "Pro"
                }
                hint={
                  isNftToken
                    ? "Requires holder indexing. Click Refresh to check for updated indexed data."
                    : "Available on advanced analytics plans. Click Refresh to check for updated indexed data."
                }
                tone={canViewAdvancedAnalytics ? "success" : "warning"}
              />

              <MetricCard
                label="Price (USD)"
                value={
                  liquidityState.isTestnet
                    ? "Unavailable"
                    : (token?.priceUsd ?? analytics?.priceUsd) != null
                      ? formatUsd(token?.priceUsd ?? analytics?.priceUsd, "Unavailable")
                      : "No price data"
                }
                hint="Depends on liquidity and indexed market data. Click Refresh after adding liquidity."
                tone="brand"
              />

              <MetricCard
                label="Transfers (24h)"
                value={
                  canViewAdvancedAnalytics
                    ? formatNumber(
                      token?.transferCount24h ?? analytics?.transferCount24h,
                      "No transfer data yet",
                    )
                    : "Pro"
                }
                hint={
                  isNftToken
                    ? "Requires transfer indexing. Krionex-confirmed actions are shown in the activity section."
                    : "Recent transfer activity where indexed data is available."
                }
                tone={canViewAdvancedAnalytics ? "success" : "warning"}
              />

              <MetricCard
                label="Volume (24h)"
                value={
                  canViewPremiumInsights
                    ? formatUsd(
                      token?.volume24hUsd ?? analytics?.volume24hUsd,
                      "Unavailable",
                    )
                    : "Pro"
                }
                hint="Available where supported liquidity or market data exists."
                tone={canViewPremiumInsights ? "success" : "warning"}
              />

              <MetricCard
                label="Market Cap"
                value={
                  canViewPremiumInsights
                    ? formatUsd(
                      token?.marketCapUsd ?? analytics?.marketCapUsd,
                      "Unavailable",
                    )
                    : "Pro"
                }
                hint="Available where supported market data exists."
                tone={canViewPremiumInsights ? "success" : "warning"}
              />

              <MetricCard
                label="Analytics Sync"
                value={analytics?.syncStatus || "Saved snapshot"}
                hint="Fresh analytics are fetched only when you click Refresh."
                tone={analytics?.syncStatus === "ok" ? "success" : "neutral"}
              />
            </div>

            {analytics?.lastSyncedAt ? (
              <p className="mt-4 text-xs text-slate-500">
                Last synced: {new Date(analytics.lastSyncedAt).toLocaleString()}
              </p>
            ) : (
              <p className="mt-4 text-xs text-slate-500">
                No saved analytics snapshot yet. Click Refresh to fetch analytics where supported.
              </p>
            )}
          </SectionCard>
        ) : (
          <SectionCard
            title="Analytics"
            subtitle="Token analytics are only available for ERC20, ERC721, and ERC1155-compatible contracts."
            eyebrow="Metrics"
          >
            <InlineNotice
              title="Token analytics are not applicable"
              subtitle={
                refreshPolicy?.message ||
                "This is a non-token custom contract. Krionex shows deployment, verification, ownership, roles, history, and explorer links, but token analytics and liquidity analytics are not applicable."
              }
              tone="neutral"
            />
          </SectionCard>
        )}

        {isDashboardToken ? (
          <SectionCard
            title="Connected wallet balance"
            subtitle={
              tokenType === "ERC1155"
                ? "Read the connected wallet balance for a specific ERC1155 Token ID."
                : tokenType === "ERC721"
                  ? "Read how many NFTs the connected wallet owns from this contract."
                  : "Read the connected wallet balance for this token contract."
            }
            eyebrow="Wallet"
            right={
              <button
                type="button"
                onClick={loadWalletBalance}
                disabled={walletBalanceLoading || !activeWallet}
                className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-[#E7D8AC] hover:bg-[#FFF9EA] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {walletBalanceLoading ? "Checking…" : "Refresh Balance"}
              </button>
            }
          >
            {!activeWallet ? (
              <InlineNotice
                title="Wallet required"
                subtitle="Connect your wallet from the top header to read your balance for this contract."
                tone="warning"
              />
            ) : tokenType === "ERC1155" ? (
              <div className="space-y-4">
                <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                    ERC1155 Token ID / Item ID
                  </div>

                  <input
                    className="mt-3 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-[#C59B45] focus:ring-4 focus:ring-[#E6C978]/20"
                    placeholder="Enter Token ID to check balance"
                    value={walletBalanceTokenId}
                    onChange={(e) => {
                      setWalletBalanceTokenId(e.target.value);
                      setWalletBalanceValue(null);
                      setWalletBalanceError("");
                    }}
                  />

                  <p className="mt-2 text-xs leading-5 text-slate-500">
                    ERC1155 balances are per Token ID. Enter the item ID, then refresh balance.
                  </p>
                </div>

                <MetricCard
                  label="Your Balance"
                  value={
                    walletBalanceLoading
                      ? "Checking…"
                      : walletBalanceValue != null
                        ? formatLargeMetricValue(walletBalanceValue)
                        : "-"
                  }
                  hint={
                    walletBalanceTokenId
                      ? `Connected wallet balance for ERC1155 Token ID ${walletBalanceTokenId}.`
                      : "Enter a Token ID to read your ERC1155 balance."
                  }
                  tone={walletBalanceValue != null ? "brand" : "neutral"}
                />

                {walletBalanceError ? (
                  <InlineNotice
                    title="Unable to read balance"
                    subtitle={walletBalanceError}
                    tone="warning"
                  />
                ) : null}
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                <MetricCard
                  label="Your Balance"
                  value={
                    walletBalanceLoading
                      ? "Checking…"
                      : walletBalanceValue != null
                        ? formatLargeMetricValue(walletBalanceValue)
                        : "-"
                  }
                  hint={
                    tokenType === "ERC721"
                      ? "For ERC721, this is how many NFTs the connected wallet owns from this contract."
                      : "Connected wallet balance for this token contract."
                  }
                  tone={walletBalanceValue != null ? "brand" : "neutral"}
                />

                <MetricCard
                  label="Connected Wallet"
                  value={shorten(activeWallet)}
                  hint="Balance is read for the currently connected wallet."
                  tone="neutral"
                />

                {walletBalanceError ? (
                  <div className="md:col-span-2">
                    <InlineNotice
                      title="Unable to read balance"
                      subtitle={walletBalanceError}
                      tone="warning"
                    />
                  </div>
                ) : null}
              </div>
            )}
          </SectionCard>
        ) : null}

        <SectionCard
          title="Krionex activity summary"
          subtitle="Confirmed wallet-signed actions recorded through Krionex for this contract."
          eyebrow="Activity"
        >
          <div className="grid gap-4 md:grid-cols-4">
            <MetricCard
              label="Mint actions"
              value={formatNumber(actionSummary.mints)}
              hint="Confirmed mint actions recorded by Krionex."
            />

            <MetricCard
              label="Burn actions"
              value={formatNumber(actionSummary.burns)}
              hint="Confirmed burn actions recorded by Krionex."
            />

            <MetricCard
              label="Pause / unpause"
              value={formatNumber(actionSummary.pauses + actionSummary.unpauses)}
              hint="Confirmed control actions for this contract."
            />

            <MetricCard
              label={isNftToken ? "Token IDs touched" : "Actions recorded"}
              value={
                isNftToken
                  ? formatNumber(actionSummary.tokenIdsTouched)
                  : formatNumber(actionSummary.total)
              }
              hint={
                isNftToken
                  ? "Unique token IDs seen in recent Krionex actions."
                  : "Total confirmed Krionex actions for this token."
              }
            />
          </div>
        </SectionCard>

        {isDashboardToken ? (
          <SectionCard
            title="Trend analytics"
            subtitle="Holder and transfer movement across recent indexed periods where network data is available."
            eyebrow="Trends"
          >
            {!canViewAdvancedAnalytics ? (
              <InlineNotice
                title="Advanced trend analytics are locked"
                subtitle="Upgrade to Starter or Pro to unlock holder and transfer trend charts."
                tone="warning"
              />
            ) : holderTrend.length === 0 && transferTrend.length === 0 ? (
              <InlineNotice
                title="Indexed trends not available yet"
                subtitle={
                  isNftToken
                    ? "NFT holder and transfer charts require indexed marketplace or chain activity. Confirmed Krionex mint, burn, pause, and unpause actions are shown in the activity sections."
                    : "Holder and transfer charts require indexed chain activity. Confirmed Krionex actions are shown in the activity sections."
                }
                tone="warning"
              />
            ) : (
              <>
                {token?.holderCount == null && token?.transferCount24h == null && (
                  <div className="mb-4">
                    <InlineNotice
                      title="Low activity token"
                      subtitle="No holder or transfer activity yet."
                      tone="warning"
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">

                  {/* HOLDER CHART */}
                  <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="mb-3">
                      <div className="text-sm font-semibold text-slate-900">
                        Holder Trend
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        Wallet holder count across recent indexed periods.
                      </div>
                    </div>

                    {holderTrend.length === 0 ? (
                      <div className="p-6 text-sm text-slate-500">
                        No holder data yet.
                      </div>
                    ) : (
                      <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={holderTrend}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                            <XAxis dataKey="label" />
                            <YAxis />
                            <Tooltip />
                            <Line
                              type="monotone"
                              dataKey="value"
                              stroke="#C59B45"
                              strokeWidth={3}
                              dot={{ r: 3 }}
                              activeDot={{ r: 5 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>

                  {/* TRANSFER CHART */}
                  <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="mb-3">
                      <div className="text-sm font-semibold text-slate-900">
                        Transfer Trend
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        Transfer activity across recent indexed periods.
                      </div>
                    </div>

                    {transferTrend.length === 0 ? (
                      <div className="p-6 text-sm text-slate-500">
                        No transfer data yet.
                      </div>
                    ) : (
                      <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={transferTrend}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                            <XAxis dataKey="label" />
                            <YAxis />
                            <Tooltip />
                            <Line
                              type="monotone"
                              dataKey="value"
                              stroke="#050713"
                              strokeWidth={3}
                              dot={{ r: 3 }}
                              activeDot={{ r: 5 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>

                </div>
              </>
            )}
          </SectionCard>
        ) : null}

        <SectionCard
          title="Deployment details"
          subtitle="Deployment metadata, verification state, enabled modules, and transaction references."
          eyebrow="Deployment"
        >
          {!deployment ? (
            <InlineNotice
              title="Deployment metadata unavailable"
              subtitle="This token may not have been deployed through Krionex, or deployment records are missing."
              tone="warning"
            />
          ) : null}

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 text-sm">
            <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-xs text-slate-500">Deployer</div>
              <div className="mt-2 font-mono break-all text-slate-900">
                {deployerAddress}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-xs text-slate-500">Verification Status</div>
              <div className="mt-2 font-semibold text-slate-900">
                {verificationStatus}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-xs text-slate-500">Deployment Tx</div>
              {deploymentTxUrl ? (
                <a
                  href={deploymentTxUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-block font-mono text-indigo-600 hover:text-indigo-700 hover:underline"
                  title={deploymentTxHash}
                >
                  {deploymentTxHash ? shorten(deploymentTxHash) : "-"}
                </a>
              ) : (
                <div className="mt-2 font-mono text-slate-900">
                  {deploymentTxHash ? shorten(deploymentTxHash) : "-"}
                </div>
              )}
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-xs text-slate-500">Modules Enabled</div>
              <div className="mt-2 font-semibold text-slate-900">
                {modules.length ? modules.join(", ") : "none"}
              </div>
            </div>
            {isNftToken ? (
              <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="text-xs text-slate-500">Royalty Receiver</div>
                <div className="mt-2 font-mono break-all text-slate-900">
                  {royaltyMetadata.receiver}
                </div>
                <div className="mt-2 text-xs text-slate-500">
                  Fee:{" "}
                  <span className="font-semibold text-slate-800">
                    {royaltyMetadata.available ? royaltyMetadata.percent : "Not set"}
                  </span>
                </div>
              </div>
            ) : null}
          </div>
        </SectionCard>

        {isErc20Token ? (
          <SectionCard
            title="Liquidity"
            subtitle="Pool visibility, reserve context, and market data where supported. Click Refresh after adding liquidity outside Krionex."
            eyebrow="Market"
          >
            <div
              className={`rounded-2xl border p-4 text-sm ${getLiquidityStatusTone(
                liquidity?.syncStatus,
              )}`}
            >
              <div className="font-semibold">
                {liquidityState.isTestnet
                  ? "Liquidity is not indexed on this network."
                  : liquidityState.isNoPool
                    ? "No liquidity pool found for this token yet."
                    : liquidityState.isAvailable
                      ? "Liquidity pool detected and indexed."
                      : liquidity?.syncStatus === "error"
                        ? "Liquidity data unavailable due to an error."
                        : "Liquidity status unavailable."}
              </div>

              <p className="mt-1 text-xs opacity-80">
                {liquidityState.isTestnet
                  ? "Liquidity data is typically unavailable on testnets."
                  : liquidityState.isNoPool
                    ? "Add liquidity on a supported DEX to enable trading data."
                    : liquidityState.isAvailable
                      ? isAdmin
                        ? "Liquidity pool data is indexed and fully visible under administrative access."
                        : "Basic liquidity visibility is included. Upgrade to unlock additional liquidity metrics where supported data is available."
                      : liquidity?.syncError || ""}
              </p>

              <div className="mt-3 flex flex-wrap gap-3">
                {liquidity?.actionUrl && liquidity?.actionLabel ? (
                  <a
                    href={liquidity.actionUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                  >
                    {liquidity.actionLabel}
                  </a>
                ) : null}

                {liquidity?.pairUrl ? (
                  <a
                    href={liquidity.pairUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                  >
                    View Pool
                  </a>
                ) : null}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4 text-sm">
              <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="text-xs text-slate-500">DEX</div>
                <div className="mt-2 font-semibold text-slate-900">
                  {liquidityState.isTestnet
                    ? "Not available on this network"
                    : liquidityState.isNoPool
                      ? "No pool yet"
                      : liquidity?.dexName || "No data"}
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="text-xs text-slate-500">Pair Address</div>
                <div className="mt-2 font-mono break-all text-slate-900">
                  {liquidityState.isTestnet
                    ? "Not available on this network"
                    : liquidityState.isNoPool
                      ? "No pool yet"
                      : liquidity?.pairAddress || "No data"}
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="text-xs text-slate-500">Base Token</div>
                <div className="mt-2 font-semibold text-slate-900">
                  {liquidityState.isTestnet
                    ? "Not available on this network"
                    : liquidityState.isNoPool
                      ? "No pool yet"
                      : liquidity?.baseTokenSymbol || "No data"}
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="text-xs text-slate-500">Quote Token</div>
                <div className="mt-2 font-semibold text-slate-900">
                  {liquidityState.isTestnet
                    ? "Not available on this network"
                    : liquidityState.isNoPool
                      ? "No pool yet"
                      : liquidity?.quoteTokenSymbol || "No data"}
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="text-xs text-slate-500">Liquidity (USD)</div>
                <div className="mt-2 font-semibold text-slate-900">
                  {liquidityState.isTestnet
                    ? "Not available on this network"
                    : liquidityState.isNoPool
                      ? "No liquidity yet"
                      : formatLiquidityUsd(
                        liquidity?.liquidityUsd,
                        !canViewLiquidityInsights,
                        "No liquidity data",
                      )}
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="text-xs text-slate-500">Reserve Base</div>
                <div className="mt-2 font-semibold text-slate-900">
                  {liquidityState.isTestnet
                    ? "Not available on this network"
                    : liquidityState.isNoPool
                      ? "No liquidity yet"
                      : formatLiquidityNumber(
                        liquidity?.reserveBase,
                        !canViewLiquidityInsights,
                        "No reserve data",
                      )}
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="text-xs text-slate-500">Reserve Quote</div>
                <div className="mt-2 font-semibold text-slate-900">
                  {liquidityState.isTestnet
                    ? "Not available on this network"
                    : liquidityState.isNoPool
                      ? "No liquidity yet"
                      : formatLiquidityNumber(
                        liquidity?.reserveQuote,
                        !canViewLiquidityInsights,
                        "No reserve data",
                      )}
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="text-xs text-slate-500">Pool Created</div>
                <div className="mt-2 font-semibold text-slate-900">
                  {liquidityState.isTestnet
                    ? "Not available on this network"
                    : liquidityState.isNoPool
                      ? "No pool yet"
                      : canViewLiquidityInsights
                        ? liquidity?.poolCreatedAt
                          ? new Date(liquidity.poolCreatedAt).toLocaleString()
                          : "No data"
                        : "Pro"}
                </div>
              </div>
            </div>

            {liquidity?.lastSyncedAt ? (
              <p className="text-xs text-slate-500">
                Last synced: {new Date(liquidity.lastSyncedAt).toLocaleString()}
              </p>
            ) : null}
          </SectionCard>

        ) : isNftToken ? (
          <SectionCard
            title="NFT market visibility"
            subtitle="Marketplace links and collection visibility for ERC721 and ERC1155 contracts. Click Refresh to fetch fresh indexed NFT data."
            eyebrow="Market"
          >
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5">

              <div className="grid gap-4 md:grid-cols-4">
                <MetricCard
                  label="Indexed NFTs"
                  value={
                    nftInsightsLoading
                      ? "Loading..."
                      : formatNumber(nftInsights?.indexedCount, "Not indexed")
                  }
                  hint="NFT items returned by Alchemy for this contract."
                />

                <MetricCard
                  label="NFT source"
                  value={nftInsights?.source ? String(nftInsights.source).toUpperCase() : "Alchemy"}
                  hint="Indexed data provider."
                />

                <MetricCard
                  label="Index status"
                  value={
                    nftInsightsLoading
                      ? "Loading..."
                      : nftInsights?.status
                        ? String(nftInsights.status).replaceAll("_", " ")
                        : "Not loaded"
                  }
                  hint="Whether indexed NFT metadata is available."
                />

                <MetricCard
                  label="Last NFT sync"
                  value={
                    nftInsights?.lastSyncedAt
                      ? new Date(nftInsights.lastSyncedAt).toLocaleString()
                      : "-"
                  }
                  hint="Last time Krionex checked indexed NFT data."
                  compactValue
                />
              </div>

              {nftInsightsError ? (
                <div className="mt-4">
                  <InlineNotice
                    title="NFT insights unavailable"
                    subtitle={nftInsightsError}
                    tone="warning"
                  />
                </div>
              ) : null}

              {Array.isArray(nftInsights?.nfts) && nftInsights.nfts.length ? (
                <div className="mt-5 rounded-3xl border border-slate-200 bg-white p-5">
                  <div className="text-sm font-semibold text-slate-950">
                    Indexed NFT samples
                  </div>

                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    {nftInsights.nfts.slice(0, 4).map((item, idx) => (
                      <div
                        key={`${item.tokenId || idx}`}
                        className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                      >
                        <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                          Token ID
                        </div>

                        <div className="mt-1 break-all text-sm font-semibold text-slate-950">
                          {item.tokenId || "-"}
                        </div>

                        <div className="mt-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                          Name
                        </div>

                        <div className="mt-1 text-sm text-slate-700">
                          {item.name || "Metadata name not indexed"}
                        </div>

                        {item.image ? (
                          <img
                            src={item.image}
                            alt={item.name || `NFT ${item.tokenId || idx}`}
                            className="mt-3 h-28 w-full rounded-xl object-cover"
                          />
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              ) : !nftInsightsLoading ? (
                <div className="mt-5">
                  <InlineNotice
                    title="NFT metadata not indexed yet"
                    subtitle="Alchemy has not returned indexed NFT metadata for this contract yet. This can happen for new deployments or metadata that has not propagated."
                    tone="warning"
                  />
                </div>
              ) : null}
              <div className="text-sm font-semibold text-slate-950">
                DEX liquidity is not applicable to this token standard.
              </div>

              <p className="mt-2 text-sm leading-6 text-slate-600">
                ERC721 and ERC1155 assets are usually listed on NFT marketplaces instead
                of ERC20 liquidity pools. Marketplace pages may appear only after
                metadata and activity are indexed.
              </p>

              {marketplaceLinks.length ? (
                <div className="mt-4 flex flex-wrap gap-3">
                  {marketplaceLinks.map((item) => (
                    <a
                      key={item.label}
                      href={item.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-[#E7D8AC] hover:bg-[#FFF9EA] hover:text-slate-950"
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              ) : (
                <div className="mt-4 text-sm text-slate-500">
                  Marketplace links are not configured for this network yet.
                </div>
              )}
            </div>
          </SectionCard>
        ) : null}

        {!isAdmin ? (
          <SectionCard
            title={premiumSectionTitle}
            subtitle={premiumSectionSubtitle}
            right={
              !isPaidPlan && !canViewPremiumInsights ? (
                <Link
                  to="/upgrade"
                  className="inline-flex items-center justify-center rounded-2xl bg-[#050713] px-4 py-3 text-sm font-semibold text-white shadow-[0_16px_35px_rgba(5,7,19,0.20)] transition hover:-translate-y-0.5 hover:bg-[#111827]"
                >
                  Upgrade Plan
                </Link>
              ) : null
            }
          >
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 text-sm">
              <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="font-semibold text-slate-900">Advanced Analytics</div>
                <div className="mt-1 text-xs text-slate-500">
                  Holder count, transfer activity, and backend-enriched usage metrics.
                </div>
                <div className="mt-3">
                  <Badge tone={canViewAdvancedAnalytics ? "success" : "warning"}>
                    {canViewAdvancedAnalytics ? "Enabled" : "Locked"}
                  </Badge>
                </div>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="font-semibold text-slate-900">Premium Insights</div>
                <div className="mt-1 text-xs text-slate-500">
                  Volume, market cap, and liquidity insights where supported network data is available.
                </div>
                <div className="mt-3">
                  <Badge tone={canViewPremiumInsights ? "success" : "warning"}>
                    {canViewPremiumInsights ? "Enabled" : "Locked"}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="mt-5 rounded-2xl border border-indigo-100 bg-gradient-to-r from-white to-indigo-50 p-4 text-sm text-slate-700">
              Current plan: <span className="font-semibold uppercase">{usageSummary.plan}</span>
            </div>
          </SectionCard>
        ) : null}

        <SectionCard
          title="Recent token actions"
          subtitle="Latest confirmed mint, burn, pause, and unpause actions for this contract."
          eyebrow="Activity"
          right={
            <Link
              to={historyRoute}
              className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-[#E7D8AC] hover:bg-[#FFF9EA] hover:text-slate-950"
            >
              View all history
            </Link>
          }
        >
          {historyLoading ? (
            <InlineNotice
              title="Loading token actions"
              subtitle="Fetching backend-confirmed token action records for this contract."
            />
          ) : historyError ? (
            <InlineNotice
              title="Unable to load token action history"
              subtitle={historyError}
              tone="error"
            />
          ) : recentTokenActions.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm leading-6 text-slate-500">
              No token action records found for this token yet.
              {isAuthed ? (
                <span className="text-slate-400">
                  {" "}
                  Confirmed mint, burn, pause, and unpause actions will appear here.
                </span>
              ) : null}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-3xl border border-slate-200 bg-white shadow-sm">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-[0.12em] text-slate-500">
                    <th className="p-3 font-medium">When</th>
                    <th className="p-3 font-medium">Action</th>
                    <th className="p-3 font-medium">Wallet / Recipient</th>
                    <th className="p-3 font-medium">Token ID</th>
                    <th className="p-3 font-medium">Amount</th>
                    <th className="p-3 font-medium">Status</th>
                    <th className="p-3 font-medium">Tx</th>
                  </tr>
                </thead>

                <tbody>
                  {recentTokenActions.map((item, idx) => {
                    const kind = String(
                      item?.kind || item?.action || item?.actionType || item?.type || "-",
                    ).toLowerCase();

                    const whenValue =
                      item?.confirmedAt ||
                      item?.createdAt ||
                      item?.updatedAt ||
                      item?.timestamp ||
                      null;

                    const whenText = whenValue
                      ? new Date(whenValue).toLocaleString()
                      : "-";

                    const walletOrRecipient = String(
                      item?.to ||
                      item?.recipient ||
                      item?.userAddress ||
                      item?.wallet ||
                      item?.from ||
                      "-",
                    );

                    const tokenId =
                      item?.tokenId != null || item?.id != null
                        ? String(item?.tokenId ?? item?.id)
                        : "-";

                    const amount =
                      item?.amountHuman != null
                        ? String(item.amountHuman)
                        : item?.amount != null
                          ? String(item.amount)
                          : item?.quantity != null
                            ? String(item.quantity)
                            : "-";

                    const status = String(
                      item?.status || item?.confirmationStatus || "confirmed",
                    );

                    const txUrl = String(
                      item?.explorerTxUrl || item?.txUrl || item?.transactionUrl || "",
                    );

                    const txHash = String(item?.txHash || item?.transactionHash || "");

                    const actionTone =
                      kind === "mint"
                        ? "success"
                        : kind === "burn"
                          ? "warning"
                          : kind === "pause" || kind === "unpause"
                            ? "brand"
                            : "neutral";

                    return (
                      <tr key={item?.id || txHash || idx} className="border-b border-slate-100 transition hover:bg-[#FFF9EA]/60">
                        <td className="p-3 whitespace-nowrap text-slate-700">
                          {whenText}
                        </td>

                        <td className="p-3">
                          <Badge tone={actionTone}>
                            {kind ? kind.toUpperCase() : "-"}
                          </Badge>
                        </td>

                        <td className="p-3 font-mono text-slate-700">
                          {shorten(walletOrRecipient)}
                        </td>

                        <td className="p-3 text-slate-700">{tokenId}</td>

                        <td className="p-3 text-slate-700">{amount}</td>

                        <td className="p-3 text-slate-700">{status}</td>

                        <td className="p-3">
                          {txUrl ? (
                            <a
                              href={txUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="font-mono text-indigo-600 hover:text-indigo-700 hover:underline"
                              title={txHash}
                            >
                              {txHash ? shorten(txHash) : "View tx"}
                            </a>
                          ) : txHash ? (
                            <span className="font-mono text-slate-700">
                              {shorten(txHash)}
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="border-t border-slate-200 px-4 py-3">
                <p className="text-xs text-slate-500">
                  Token actions shown here are backend-confirmed from wallet-signed blockchain transactions.
                  Use the transaction link to verify mint, burn, pause, or unpause directly on the explorer.
                </p>
              </div>
            </div>
          )}
        </SectionCard>
        {actionModal ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 px-4 backdrop-blur-sm">
            <div className="w-full max-w-lg rounded-3xl border border-white/70 bg-white p-6 shadow-[0_30px_90px_rgba(15,23,42,0.28)]">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="inline-flex rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-indigo-700">
                    Token Action
                  </div>

                  <h3 className="mt-3 text-xl font-semibold tracking-tight text-slate-900 capitalize">
                    {actionModal}
                  </h3>

                  <p className="mt-2 text-sm leading-6 text-slate-500">
                    This action will open your wallet and submit an on-chain
                    transaction. Gas fees are paid by your wallet and are not
                    refundable by Krionex.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={closeActionModal}
                  disabled={actionLoading}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
                >
                  Close
                </button>
              </div>

              {actionModal === "burn" ? (
                <div className="mt-5 space-y-4">
                  {tokenType === "ERC721" || tokenType === "ERC1155" ? (
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                        Token ID
                      </label>
                      <input
                        value={actionTokenId}
                        onChange={(e) => setActionTokenId(e.target.value)}
                        placeholder="Example: 1"
                        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
                      />
                    </div>
                  ) : null}

                  {tokenType === "ERC20" || tokenType === "ERC1155" ? (
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                        Amount
                      </label>
                      <input
                        value={actionAmount}
                        onChange={(e) => setActionAmount(e.target.value)}
                        placeholder={
                          tokenType === "ERC20" ? "Example: 100" : "Example: 1"
                        }
                        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
                      />
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="mt-5 rounded-2xl border border-violet-100 bg-violet-50/70 p-4 text-sm leading-6 text-violet-900">
                  {actionModal === "pause"
                    ? "Pause will attempt to stop protected token operations until the owner unpauses the contract."
                    : "Unpause will attempt to restore protected token operations."}
                </div>
              )}

              <label className="mt-5 flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-[13px] leading-6 text-slate-700">
                <input
                  type="checkbox"
                  checked={acceptedActionRisk}
                  onChange={(e) => {
                    setAcceptedActionRisk(e.target.checked);
                    if (e.target.checked) setActionStatus("");
                  }}
                  className="mt-1 h-4 w-4 rounded border-slate-300"
                />
                <span>
                  I understand this is an irreversible blockchain transaction. I
                  am responsible for wallet confirmation, gas fees, selected
                  network, token ownership, legality, and compliance.{" "}
                  <Link
                    to="/terms"
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-indigo-700 hover:text-indigo-800"
                  >
                    Terms
                  </Link>
                </span>
              </label>

              {actionStatus ? (
                <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 text-sm leading-6 text-slate-700">
                  {actionStatus}
                </div>
              ) : null}

              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={handleTokenAction}
                  disabled={actionLoading || !acceptedActionRisk}
                  className="inline-flex flex-1 items-center justify-center rounded-2xl bg-gradient-to-r from-indigo-600 via-violet-600 to-sky-600 px-4 py-3 text-sm font-semibold text-white shadow-[0_14px_35px_rgba(79,70,229,0.28)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {actionLoading ? "Processing…" : `Confirm ${actionModal}`}
                </button>

                <button
                  type="button"
                  onClick={closeActionModal}
                  disabled={actionLoading}
                  className="inline-flex flex-1 items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}